import React, { useState, useEffect, useContext, useCallback, memo } from 'react';
import axios from 'axios';
import { openServerPdf, askPdfOrXlsx } from '../utils/pdfPrint';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { AuthContext, LanguageContext, API, formatDateWithEnglishNumerals, formatTimeWithEnglishNumerals } from '../shared';
import LifecycleBadge from './LifecycleBadge';
import SubagentApprovalDialog from './SubagentApprovalDialog';
import { GrantCreditNoteDialog } from './CreditNotesManagement';
import SubsequentAdjustmentsPanel from './SubsequentAdjustmentsPanel';
import { Plus, Search, Edit, Trash2, FileText, Download, Printer, Eye, Upload, Calendar, DollarSign, Building2, Users, MapPin, AlertTriangle, Check, X, RefreshCw, ArrowRightLeft, ChevronDown, ChevronUp, Filter, BarChart3, CheckCircle, Info, XCircle, Clock } from 'lucide-react';

const DailyOperationsManagement = () => {
  const { t } = useContext(LanguageContext);
  const { user } = useContext(AuthContext);
  // 🆕 Credit note (exceptional discount after full settlement) target.
  const [creditNoteFor, setCreditNoteFor] = useState(null);

  // ============================================================
  // Async confirm/prompt dialogs (replace browser-blocked
  // window.confirm and window.prompt). Returns a Promise<boolean>
  // for confirm and Promise<string|null> for prompt. Backed by
  // existing shadcn Dialog so it works in every browser/iframe.
  // ============================================================
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    confirmLabel: 'تأكيد',
    cancelLabel: 'إلغاء',
    danger: false,
    resolver: null,
  });
  const [promptDialog, setPromptDialog] = useState({
    open: false,
    title: '',
    message: '',
    placeholder: '',
    defaultValue: '',
    multiline: false,
    value: '',
    resolver: null,
  });

  const confirmAsync = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setConfirmDialog({
        open: true,
        title: opts.title || 'تأكيد العملية',
        message,
        confirmLabel: opts.confirmLabel || 'تأكيد',
        cancelLabel: opts.cancelLabel || 'إلغاء',
        danger: opts.danger || false,
        resolver: resolve,
      });
    });
  }, []);

  const promptAsync = useCallback((message, defaultValue = '', opts = {}) => {
    return new Promise((resolve) => {
      setPromptDialog({
        open: true,
        title: opts.title || 'إدخال',
        message,
        placeholder: opts.placeholder || '',
        defaultValue,
        multiline: opts.multiline || false,
        value: defaultValue,
        resolver: resolve,
      });
    });
  }, []);

  const closeConfirm = (result) => {
    setConfirmDialog((prev) => {
      if (prev.resolver) prev.resolver(result);
      return { ...prev, open: false, resolver: null };
    });
  };

  const closePrompt = (result) => {
    setPromptDialog((prev) => {
      if (prev.resolver) prev.resolver(result);
      return { ...prev, open: false, resolver: null };
    });
  };

  const [operations, setOperations] = useState([]);
  const [services, setServices] = useState([]);
  const [clients, setClients] = useState([]);
  const [agencies, setAgencies] = useState([]);
  // 🆕 Available seasons for the filter dropdown
  const [availableSeasons, setAvailableSeasons] = useState([
    { value: 'current', label: 'الموسم الحالي' },
    { value: 'all', label: 'كل المواسم' },
  ]);
  const [loading, setLoading] = useState(true);

  // Pagination — Daily Operations table is heavy. Server returns 50 rows per
  // request sorted by created_at DESC (newest first) so accountants see the
  // latest entries immediately, and the page loads in <1s instead of 5–10s.
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  // When a cross-link badge ("⛔ استرجاع" / "🔄 مُسترَجَع") needs to jump to
  // a row that lives on another page, we stash the target ID here. After the
  // next fetch finishes (operations refreshed for the new page), an effect
  // scrolls + highlights the row and clears this state.
  const [pendingScrollId, setPendingScrollId] = useState(null);

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showPrintPreview, setShowPrintPreview] = useState(false);
  // Duplicate detection: when the backend returns 409, we stash the
  // suspected duplicate info + the original form payload so the user can
  // confirm and force-create with a single click.
  const [duplicateWarning, setDuplicateWarning] = useState(null); // { detail, payload, image }
  // === Refund flow (any service operation) ===
  const [refundOp, setRefundOp] = useState(null);
  const [refundAmount, setRefundAmount] = useState('');
  const [refundReason, setRefundReason] = useState('');
  const [refundImage, setRefundImage] = useState(null);
  const [refundBusy, setRefundBusy] = useState(false);
  const [selectedOperationForPrint, setSelectedOperationForPrint] = useState(null);
  const [editingOperation, setEditingOperation] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  
  // NEW: Payment states
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [selectedOperationForPayment, setSelectedOperationForPayment] = useState(null);
  const [paymentFormData, setPaymentFormData] = useState({
    method: 'cash',
    amount: '',
    payment_date: new Date().toISOString().split('T')[0],
    notes: ''
  });
  const [operationPaymentStatuses, setOperationPaymentStatuses] = useState({});
  
  // NEW: Installment integration states
  const [installmentPlans, setInstallmentPlans] = useState([]);
  const [installmentLoading, setInstallmentLoading] = useState(false);
  
  // NEW: Current operation payments management
  const [currentOperationPayments, setCurrentOperationPayments] = useState([]);
  const [showPaymentsManagement, setShowPaymentsManagement] = useState(false);

  // NEW: Convert remaining to installments
  const [showConvertDialog, setShowConvertDialog] = useState(false);
  const [convertOperation, setConvertOperation] = useState(null);
  const [convertRemaining, setConvertRemaining] = useState(0);
  const [installmentNextDue, setInstallmentNextDue] = useState({});
  const [convertForm, setConvertForm] = useState({
    number_of_payments: 3,
    start_date: new Date().toISOString().split('T')[0],
    payment_frequency: 'monthly',
    custom_dates: []
  });
  const [converting, setConverting] = useState(false);

  const openConvertDialog = (operation, remaining) => {
    setConvertOperation(operation);
    setConvertRemaining(remaining);
    setConvertForm({
      number_of_payments: 3,
      start_date: new Date().toISOString().split('T')[0],
      payment_frequency: 'monthly',
      custom_dates: []
    });
    setShowConvertDialog(true);
  };

  const handleRefreshServicePrice = async (operation) => {
    const ok = await confirmAsync(
      `سيتم تحديث سعر هذه العملية إلى السعر الحالي للخدمة "${operation.service_name}".\n\n` +
      `العملية: ${operation.operation_no}\n` +
      `السعر الحالي: ${(operation.base_price || 0).toLocaleString()} دج\n\n` +
      `⚠️ ملاحظة: إذا كان السعر الجديد أقل من المبلغ المدفوع، سيتم إنشاء رصيد للاسترجاع تلقائياً.\n\n` +
      `هل تريد المتابعة؟`,
      { title: 'تحديث سعر العملية', confirmLabel: 'نعم، حدّث' }
    );
    if (!ok) return;
    try {
      const r = await axios.post(
        `${API}/daily-operations/${operation.id}/refresh-service-price`,
        {}
      );
      if (r.data.changed) {
        let msg =
          `✅ ${r.data.message}\n\n` +
          `السعر القديم: ${(r.data.old_price || 0).toLocaleString()} دج\n` +
          `السعر الجديد: ${(r.data.new_price || 0).toLocaleString()} دج`;
        if (r.data.needs_refund && r.data.credit_amount > 0) {
          msg += `\n\n💰 رصيد للاسترجاع: ${(r.data.credit_amount || 0).toLocaleString()} دج\n` +
                 `سيظهر زر "↩️ استرجاع" بجانب العملية للمتابعة.`;
        }
        alert(msg);
        await fetchOperations();
      } else {
        alert(`ℹ️ ${r.data.message}`);
      }
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleProcessRefundCredit = (operation) => {
    // Write to sessionStorage FIRST so RefundManagement's mount-effect sees it
    try {
      sessionStorage.setItem('refund_pending_operation', JSON.stringify({
        operation_id: operation.id,
        operation_no: operation.operation_no,
        client_id: operation.client_id,
        client_name: operation.client_name,
        service_name: operation.service_name,
        credit_amount: operation.pending_refund_credit || 0,
        agency_id: operation.agency_id,
      }));
    } catch (e) { /* ignore */ }
    window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'refunds' }));
  };

  // Sanitize a phone number to international E.164 (digits only). Defaults
  // missing country codes to Algeria (+213) so wa.me works out of the box.
  const normalizePhoneForWa = (raw) => {
    if (!raw) return '';
    let s = String(raw).replace(/[^0-9+]/g, '');
    if (s.startsWith('+')) s = s.slice(1);
    if (!s) return '';
    // Common Algerian leading-zero numbers (e.g., 0555...) → 213555...
    if (s.startsWith('00')) s = s.slice(2);
    else if (s.startsWith('0')) s = '213' + s.slice(1);
    return s;
  };

  const handleShareViaWhatsApp = async (operation) => {
    try {
      // Try to grab the client's phone — fall back to a manual prompt
      let phone = operation.client_phone || operation.phone || '';
      if (!phone && operation.client_id) {
        try {
          const cr = await axios.get(`${API}/clients/${operation.client_id}`);
          phone = cr.data?.phone || '';
        } catch (e) { /* ignore */ }
      }
      const phoneClean = normalizePhoneForWa(phone);
      let finalPhone = phoneClean;
      if (!finalPhone) {
        const entered = await promptAsync(
          'أدخل رقم هاتف العميل (مثال: 213555123456):',
          '',
          { title: 'إرسال PDF عبر واتساب', placeholder: '213555123456' }
        );
        finalPhone = (entered || '').replace(/[^0-9]/g, '');
      }
      if (!finalPhone) return;

      const agencyName = operation.agency_name || 'وكالة صنهاجة';
      const serviceName = operation.service_name || 'الخدمة';
      const fileName = `وصل-${operation.operation_no || operation.id}.pdf`;

      // Fetch the actual PDF bytes from the backend (with auth) so we can
      // share the document itself instead of a public link. Using the share
      // token endpoint means we don't need an Authorization header on the
      // download — important for some browsers' Web Share API.
      const tok = await axios.post(`${API}/pdf-share/create`, { operation_id: operation.id });
      const relative = tok.data.relative_url; // e.g. /api/pdf-share/<token>
      const backend = process.env.REACT_APP_BACKEND_URL || '';
      const pdfUrl = `${backend.replace(/\/$/, '')}${relative}`;

      const pdfResp = await fetch(pdfUrl);
      if (!pdfResp.ok) throw new Error('PDF download failed: ' + pdfResp.status);
      const pdfBlob = await pdfResp.blob();
      const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

      // Short message — no link to the website. The PDF itself is attached.
      const messageText =
        `السلام عليكم،\n` +
        `تجدون مرفقاً وصل ${serviceName}.\n\n` +
        `شكراً لثقتكم،\n` +
        `${agencyName}`;

      // Strategy:
      //   1) Mobile: Web Share API can attach files → opens native share sheet
      //      (WhatsApp appears in the list and the PDF is sent as attachment).
      //   2) Desktop fallback: download the PDF locally, then open
      //      WhatsApp Web with just the text. The user drags the downloaded
      //      PDF into the chat (one extra step, but the customer no longer
      //      sees a sanhadja.org link).
      const canShareFiles =
        typeof navigator !== 'undefined' &&
        typeof navigator.canShare === 'function' &&
        typeof navigator.share === 'function' &&
        navigator.canShare({ files: [pdfFile] });

      if (canShareFiles) {
        try {
          await navigator.share({
            files: [pdfFile],
            text: messageText,
            title: fileName,
          });
          return; // success
        } catch (shareErr) {
          // User cancelled — silently fall through to desktop fallback
          if (shareErr && shareErr.name === 'AbortError') return;
          console.warn('Web Share failed, falling back:', shareErr);
        }
      }

      // Desktop fallback: download the PDF + open wa.me with just the text.
      const blobUrl = URL.createObjectURL(pdfBlob);
      const dl = document.createElement('a');
      dl.href = blobUrl;
      dl.download = fileName;
      document.body.appendChild(dl);
      dl.click();
      document.body.removeChild(dl);
      setTimeout(() => URL.revokeObjectURL(blobUrl), 5000);

      const waUrl = `https://wa.me/${finalPhone}?text=${encodeURIComponent(messageText)}`;
      const a = document.createElement('a');
      a.href = waUrl;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      // Tell the user what to do next on desktop
      alert(
        '✅ تم تنزيل الـ PDF وفتح واتساب.\n\n' +
        `📎 اسحب الملف "${fileName}" من تنزيلات المتصفح إلى محادثة الواتساب لإرساله كمرفق (بدون رابط).`
      );
    } catch (e) {
      alert('❌ فشل إرسال PDF عبر الواتساب: ' + (e.response?.data?.detail || e.message));
    }
  };

  const handleConvertSubmit = async () => {
    if (!convertOperation) return;
    if (convertForm.payment_frequency === 'custom') {
      const dates = convertForm.custom_dates || [];
      if (dates.filter(Boolean).length !== Number(convertForm.number_of_payments)) {
        alert('يجب إدخال جميع التواريخ المخصصة');
        return;
      }
    }
    setConverting(true);
    try {
      const payload = {
        number_of_payments: Number(convertForm.number_of_payments),
        start_date: convertForm.start_date,
        payment_frequency: convertForm.payment_frequency,
      };
      if (convertForm.payment_frequency === 'custom') {
        payload.custom_dates = convertForm.custom_dates;
      }
      const r = await axios.post(
        `${API}/daily-operations/${convertOperation.id}/convert-to-installments`,
        payload
      );
      alert(`✅ ${r.data.message}\nرقم الوصل: ${r.data.receipt_no}\nالمبلغ: ${r.data.amount_converted.toLocaleString()} دج`);
      setShowConvertDialog(false);
      setConvertOperation(null);
      // Refresh & navigate
      await fetchOperations();
      if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('navigate-to-tab', { detail: 'simple-installments' }));
      }
    } catch (e) {
      alert('❌ ' + (e.response?.data?.detail || e.message));
    } finally {
      setConverting(false);
    }
  };
  
  // NEW: Image management states
  const [showImageDialog, setShowImageDialog] = useState(false);
  const [selectedOperationForImage, setSelectedOperationForImage] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  
  const [printDetails, setPrintDetails] = useState({
    paymentType: 'نقدي', // نقدي، بنكي، قسط
    amountPaid: 0,
    remainingAmount: 0,
    paymentStatus: 'مدفوع كاملاً' // مدفوع كاملاً، دفعة مقدمة، مؤجل
  });
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);  // Add this for client dialog
  const [clientFormData, setClientFormData] = useState({  // Add this for client form
    name: '',
    phone: '',
    cin_passport: '',
    email: '',
    address: ''
  });
  const [formData, setFormData] = useState({
    service_id: '',
    client_id: '',
    base_price: '', // Allow custom price for variable services
    discount_amount: 0,
    discount_reason: '',
    notes: '',
    // NEW: Enhanced document system
    document_image: null,
    document_type: 'image', // 'image' or 'pdf'
    // NEW: Payment system
    payment_type: 'full', // 'full' or 'installment'
    first_payment_amount: '',
    first_payment_date: new Date().toISOString().split('T')[0],
    second_payment_amount: '',
    second_payment_date: '',
    third_payment_amount: '',
    third_payment_date: '',
    fourth_payment_amount: '',
    fourth_payment_date: '',
    // NEW: Flexible installment fields
    flexible_installments: 6,
    installment_period: 'monthly',
    first_payment_flexible: '',
    // 🆕 Supplier integration (option B): mark this operation as a
    // supplier-resold service. When `service_origin === 'supplier'`,
    // we auto-create a SALE_DUE entry in the supplier ledger after the
    // operation is successfully created.
    service_origin: 'internal',  // 'internal' = خدمة صنهاجة | 'supplier' = خدمة مورد
    supplier_id: '',
    supplier_service_id: '',
    supplier_cost: '',  // tracked separately from base_price (sell price)
  });

  // NEW: Advanced Filtering State
  const [filters, setFilters] = useState({
    agency_id: 'all',
    client_name: '',
    service_name: '',
    service_type: 'all',
    status: 'all',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
    supplier_id: 'all',  // 🆕 filter by supplier
    season: 'current'   // 🆕 default: only the current (untagged) season
  });
  // Active filter set actually sent to the backend. We keep this separate
  // from the form state (`filters`) so that draft edits don't trigger a fetch
  // until the user clicks "Apply", and so that pagination useEffects keep
  // applying the same filter on every page change.
  const [activeFilters, setActiveFilters] = useState({ season: 'current' });
  const [showFilters, setShowFilters] = useState(false);
  const [filteredOperations, setFilteredOperations] = useState([]);
  // Always-visible quick search across operation_no / client / service
  const [quickSearch, setQuickSearch] = useState('');
  // Memoized list of operations after applying quick search.
  // Computed at the top of the component (NOT inside the JSX as an IIFE) so
  // React reliably re-binds the onClick handlers when the list changes.
  const visibleOperations = React.useMemo(() => {
    const q = (quickSearch || '').trim().toLowerCase();
    if (!q) return operations;
    return operations.filter(op => {
      const opNo = (op.operation_no || '').toLowerCase();
      const cName = (op.client_name || '').toLowerCase();
      const sName = (op.service_name || '').toLowerCase();
      return opNo.includes(q) || cName.includes(q) || sName.includes(q);
    });
  }, [operations, quickSearch]);

  // Check if user can approve operations
  const canApproveOperations = user?.role === 'super_admin' || user?.role === 'general_accountant';
  // 🆕 Supplier integration state (Phase 1)
  const [suppliers, setSuppliers] = useState([]);
  const [supplierServices, setSupplierServices] = useState([]);
  const isManagement = ['super_admin', 'general_accountant', 'general_manager'].includes(user?.role);

  useEffect(() => {
    // 🆕 Honor a "pending-intent" set by the Dashboard pending-approvals
    // chip: when the user lands here via that chip, auto-filter to only
    // the rows that need approval. We do this BEFORE the first fetch so
    // the user doesn't see "all operations" flash on screen.
    const intent = sessionStorage.getItem('pending-intent');
    if (intent === 'daily_operations') {
      sessionStorage.removeItem('pending-intent');
      const pendingFilter = { status: 'في انتظار الموافقة', season: 'current' };
      setFilters(prev => ({ ...prev, status: 'في انتظار الموافقة' }));
      setActiveFilters(pendingFilter);
      setShowFilters(true);
      fetchOperations(pendingFilter);
    } else {
      fetchOperations();
    }
    fetchServices();
    fetchClients();
    fetchAgencies();
    // 🆕 Load suppliers list once for the "خدمة مورد" picker
    (async () => {
      try {
        const r = await axios.get(`${API}/suppliers?status=active`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        setSuppliers(r.data || []);
      } catch { /* non-fatal */ }
    })();
    // 🆕 Load available seasons for the dropdown
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/season/available`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        });
        if (r.data?.items?.length) setAvailableSeasons(r.data.items);
      } catch (e) {
        // Non-fatal — keep the default "current/all" pair
        console.warn('season list failed:', e.message);
      }
    })();
  }, []);

  // Re-fetch when pagination state changes
  useEffect(() => {
    fetchOperations();
  }, [page, pageSize]);

  // Auto-refresh: poll the operations list every 30 seconds so that operations
  // newly created by other staff become visible to accountants/admins without
  // requiring a manual page refresh. Pauses while a dialog/modal is open
  // (we infer that from the body class added by Radix dialogs).
  // Auto-refresh only kicks in on page 1 — if you're browsing older pages we
  // don't yank the rows from under you.
  useEffect(() => {
    const interval = setInterval(() => {
      // Skip refresh if user is editing/typing in a dialog or has filters open
      // — this avoids stomping on in-progress work.
      const dialogOpen = document.querySelector('[role="dialog"][data-state="open"]');
      if (dialogOpen) return;
      if (page !== 1) return;
      // Pull silently (no loading spinner). fetchOperations() with no args
      // re-uses the currently active filters automatically.
      fetchOperations();
    }, 30000); // 30 seconds
    return () => clearInterval(interval);
  }, [page, activeFilters]);

  // Update payment statuses when operations change
  useEffect(() => {
    if (operations.length > 0) {
      updatePaymentStatuses();
    }
  }, [operations]);

  const fetchOperations = async (filterParams = null) => {
    try {
      // When a caller explicitly passes filterParams use them, otherwise
      // re-use the last applied filters so paging doesn't clear the filter.
      const effectiveFilters = filterParams === null ? activeFilters : filterParams;

      // Build query parameters
      const params = new URLSearchParams();

      // Add filter parameters
      Object.entries(effectiveFilters || {}).forEach(([key, value]) => {
        if (value && value !== '') {
          params.append(key, value);
        }
      });

      // Pagination — always request paginated response so the server returns
      // the {items,total,total_pages,...} envelope. The backend sorts by
      // created_at DESC, so newest entries are always at the top.
      params.set('page', String(page));
      params.set('page_size', String(pageSize));

      const queryString = params.toString();
      const url = `${API}/daily-operations${queryString ? `?${queryString}` : ''}`;

      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });

      // Tolerate both shapes: paginated envelope OR legacy bare list.
      const payload = response.data;
      const fetchedItems = Array.isArray(payload) ? payload : (payload.items || []);
      const total = Array.isArray(payload) ? fetchedItems.length : (payload.total ?? fetchedItems.length);
      const tPages = Array.isArray(payload) ? 1 : (payload.total_pages ?? 1);

      setTotalCount(total);
      setTotalPages(tPages);

      // Re-sort defensively so newest is on top regardless of any server quirk.
      const sortedOperations = [...fetchedItems].sort((a, b) => {
        return new Date(b.created_at || b.date) - new Date(a.created_at || a.date);
      });

      setOperations(sortedOperations);
      setFilteredOperations(sortedOperations);

      // Fetch next-due summary for all converted operations (single bulk call)
      const receiptIds = sortedOperations
        .map(o => o.installment_receipt_id)
        .filter(Boolean);
      if (receiptIds.length > 0) {
        try {
          const uniq = Array.from(new Set(receiptIds));
          const r = await axios.get(
            `${API}/installment-receipts/next-due-summary`,
            { params: { receipt_ids: uniq.join(',') } }
          );
          setInstallmentNextDue(r.data || {});
        } catch (e) {
          // Silent — UI will show "..." placeholder
        }
      } else {
        setInstallmentNextDue({});
      }
    } catch (error) {
      console.error('Error fetching operations:', error);
    } finally {
      setLoading(false);
    }
  };

  // Add client function
  const handleAddClient = async () => {
    if (!clientFormData.name || !clientFormData.phone) {
      alert('يرجى إدخال الاسم ورقم الهاتف على الأقل');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/clients`, clientFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('✅ تم إضافة العميل بنجاح');
      
      // Update clients list
      setClients([...clients, response.data]);
      
      // Set the new client as selected
      setFormData({...formData, client_id: response.data.id});
      
      // Reset form and close dialog
      setClientFormData({
        name: '',
        phone: '',
        cin_passport: '',
        email: '',
        address: ''
      });
      setShowAddClientDialog(false);
      
      // Reopen operation dialog and select the new client
      setTimeout(() => {
        setShowAddDialog(true);
      }, 100);
      
    } catch (error) {
      console.error('Error adding client:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      // Better error message handling
      let errorMessage = 'خطأ غير معروف';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data) {
        errorMessage = JSON.stringify(error.response.data);
      }
      
      alert('❌ فشل في إضافة العميل: ' + errorMessage);
    }
  };

  // Apply filters to operations
  const applyFilters = () => {
    const filterParams = {};
    
    // Build filter parameters (exclude "all" values)
    if (filters.agency_id && filters.agency_id !== 'all') filterParams.agency_id = filters.agency_id;
    if (filters.client_name) filterParams.client_name = filters.client_name;
    if (filters.service_name) filterParams.service_name = filters.service_name;
    if (filters.service_type && filters.service_type !== 'all') filterParams.service_type = filters.service_type;
    if (filters.status && filters.status !== 'all') filterParams.status = filters.status;
    if (filters.start_date) filterParams.start_date = filters.start_date;
    if (filters.end_date) filterParams.end_date = filters.end_date;
    if (filters.min_amount) filterParams.min_amount = filters.min_amount;
    if (filters.max_amount) filterParams.max_amount = filters.max_amount;
    if (filters.supplier_id && filters.supplier_id !== 'all') filterParams.supplier_id = filters.supplier_id;
    // 🆕 Season filter — default is 'current' (untagged rows only).
    if (filters.season) filterParams.season = filters.season;

    setActiveFilters(filterParams);
    // Reset to page 1 whenever filters change so the user sees the matching
    // results from the start (the useEffect on `page` will trigger fetch).
    if (page !== 1) {
      setPage(1);
    } else {
      fetchOperations(filterParams);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setFilters({
      agency_id: 'all',
      client_name: '',
      service_name: '',
      service_type: 'all',
      status: 'all',
      start_date: '',
      end_date: '',
      min_amount: '',
      max_amount: '',
      supplier_id: 'all',
      season: 'current'
    });
    setActiveFilters({});
    if (page !== 1) {
      setPage(1);
    } else {
      fetchOperations({});
    }
  };

  // NEW: Payment management functions
  const fetchOperationPaymentStatus = async (operationId) => {
    try {
      const response = await axios.get(`${API}/daily-operations/${operationId}/payment-status`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      return response.data;
    } catch (error) {
      console.error('Error fetching payment status:', error);
      return null;
    }
  };

  const updatePaymentStatuses = async () => {
    const statuses = {};
    for (const operation of operations) {
      const status = await fetchOperationPaymentStatus(operation.id);
      if (status) {
        statuses[operation.id] = status;
      }
    }
    setOperationPaymentStatuses(statuses);
  };

  const handleAddPayment = (operation) => {
    setSelectedOperationForPayment(operation);
    const status = operationPaymentStatuses[operation.id];
    setPaymentFormData({
      method: 'cash',
      amount: status?.remaining_amount?.toString() || operation.final_price.toString(),
      payment_date: new Date().toISOString().split('T')[0],
      notes: ''
    });
    setShowPaymentDialog(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedOperationForPayment) return;
    
    try {
      console.log('=== ADDING PAYMENT TO OPERATION ===');
      console.log('Operation ID:', selectedOperationForPayment.id);
      console.log('Payment data:', paymentFormData);
      
      await axios.post(
        `${API}/daily-operations/${selectedOperationForPayment.id}/payments`,
        paymentFormData,
        {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        }
      );
      
      console.log('=== PAYMENT ADDED SUCCESSFULLY ===');
      alert('✅ تم إضافة المدفوعة بنجاح');
      
      // Reset form and close dialog
      setShowPaymentDialog(false);
      setSelectedOperationForPayment(null);
      setPaymentFormData({
        method: 'cash',
        amount: '',
        payment_date: new Date().toISOString().split('T')[0],
        notes: ''
      });
      
      // Refresh operations and payment statuses
      fetchOperations();
      setTimeout(updatePaymentStatuses, 1000);
      
    } catch (error) {
      console.error('=== PAYMENT ERROR ===');
      console.error('Error adding payment:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لإضافة مدفوعات لهذه العملية');
      } else if (error.response?.status === 400) {
        alert('❌ خطأ في البيانات: ' + (error.response?.data?.detail || error.message));
      } else {
        alert('❌ فشل في إضافة المدفوعة: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const getPaymentStatusBadge = (operationId, operation) => {
    const status = operationPaymentStatuses[operationId];
    
    // Special handling for installment operations
    if (operation?.operation_no?.startsWith('INST-')) {
      return <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium">📋 قسط مدفوع</span>;
    }
    
    if (operation?.payment_type === 'flexible_installment') {
      return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">🌟 تقسيط مريح</span>;
    }
    
    if (operation?.payment_type === 'installment') {
      return <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded-full text-xs font-medium">📅 دفعة تقسيط</span>;
    }
    
    // Regular payment status for normal operations
    if (!status) return <span className="text-gray-500">⏳ جاري التحميل...</span>;
    
    switch (status.payment_status) {
      case 'fully_paid':
        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">🟢 مدفوع كاملاً</span>;
      case 'partially_paid':
        return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">🟡 مدفوع جزئياً</span>;
      case 'unpaid':
        return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">🔴 غير مدفوع</span>;
      default:
        return <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs font-medium">❓ غير محدد</span>;
    }
  };

  // NEW: Image modal states
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [modalImageUrl, setModalImageUrl] = useState('');
  const [modalImageTitle, setModalImageTitle] = useState('');

  // NEW: View document function
  const viewDocument = (documentUrl, documentType, operationNo) => {
    if (!documentUrl) {
      alert('لا توجد وثيقة لعرضها');
      return;
    }

    const fullUrl = `${API}${documentUrl}`;
    
    if (documentType === 'pdf') {
      // For PDFs, open in new tab/window
      const newWindow = window.open(fullUrl, '_blank');
      if (!newWindow) {
        alert('يرجى السماح للنوافذ المنبثقة لعرض ملف PDF');
      }
    } else if (documentType === 'image') {
      // For images, show in a modal
      setModalImageUrl(fullUrl);
      setModalImageTitle(`وثيقة العملية ${operationNo}`);
      setShowDocumentModal(true);
    } else {
      // For unknown types, try to open in new window
      const newWindow = window.open(fullUrl, '_blank');
      if (!newWindow) {
        alert('يرجى السماح للنوافذ المنبثقة لعرض الوثيقة');
      }
    }
  };

  const fetchServices = async () => {
    try {
      const response = await axios.get(`${API}/services?is_active=true`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setServices(response.data);
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      // Sort clients by creation date - newest first
      const sortedClients = response.data.sort((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });
      setClients(sortedClients);
    } catch (error) {
      console.error('Error fetching clients:', error);
    }
  };

  const fetchAgencies = async () => {
    try {
      const response = await axios.get(`${API}/agencies`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      setAgencies(response.data);
    } catch (error) {
      console.error('Error fetching agencies:', error);
    }
  };

  const handleServiceChange = (serviceId) => {
    const service = services.find(s => s.id === serviceId);
    setSelectedService(service);
    
    // Set default price for fixed-price services, clear for variable services
    const basePrice = service && service.is_fixed_price ? service.base_price.toString() : '';
    
    // 🆕 Auto-populate supplier metadata from the service's own origin tag.
    // This way the catalog acts as the single source of truth: if a service
    // is registered in the catalog as a supplier resale, the operation just
    // inherits that tagging instead of forcing the user to re-pick.
    const isSupplier = service?.origin === 'supplier' && service?.supplier_id;
    setFormData({
      ...formData,
      service_id: serviceId,
      base_price: basePrice,
      service_origin: isSupplier ? 'supplier' : 'internal',
      supplier_id: isSupplier ? service.supplier_id : '',
      supplier_service_id: '',
      supplier_cost: isSupplier && service.supplier_cost != null ? String(service.supplier_cost) : '',
    });
  };

  /**
   * Attempts to create a daily operation. If the backend reports a possible
   * duplicate (HTTP 409 with code=DUPLICATE_SUSPECTED), we open the warning
   * modal instead and return null so the caller can short-circuit.
   *
   * Setting `force=true` skips the duplicate check on the backend — used by
   * the "نعم، أكمل الإضافة" button in the modal.
   */
  const createOperationWithDuplicateGuard = async (payload, image, force = false) => {
    try {
      const url = `${API}/daily-operations${force ? '?force=true' : ''}`;
      return await axios.post(url, payload, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.detail?.code === 'DUPLICATE_SUSPECTED') {
        setDuplicateWarning({ detail: err.response.data.detail, payload, image });
        return null;
      }
      throw err;
    }
  };

  /** Re-submits with force=true after the user confirms in the modal. */
  const confirmCreateDespiteDuplicate = async () => {
    if (!duplicateWarning) return;
    const { payload, image } = duplicateWarning;
    setDuplicateWarning(null);
    try {
      const response = await createOperationWithDuplicateGuard(payload, image, true);
      if (!response) return;
      if (image) {
        const fd = new FormData();
        fd.append('image', image);
        await axios.post(`${API}/daily-operations/${response.data.id}/upload-image`, fd, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
            'Content-Type': 'multipart/form-data',
          },
        });
      }
      alert('✅ تم إنشاء العملية بنجاح (تم تجاوز التحذير)');
      setShowAddDialog(false);
      setEditingOperation(null);
      setSelectedService(null);
      fetchOperations();
    } catch (e) {
      alert('❌ فشل إنشاء العملية: ' + (e.response?.data?.detail || e.message));
    }
  };

  /** Open refund modal for a daily operation row. */
  const openOperationRefund = (op) => {
    setRefundOp(op);
    setRefundAmount(String(op.final_price || ''));
    setRefundReason('');
    setRefundImage(null);
  };

  /** Submit operation refund: amount + reason + mandatory image. */
  const submitOperationRefund = async () => {
    if (!refundOp) return;
    const amt = parseFloat(refundAmount);
    if (!amt || amt <= 0) { alert('⚠️ المبلغ يجب أن يكون أكبر من 0'); return; }
    if (!refundReason || refundReason.trim().length < 5) {
      alert('⚠️ السبب يجب أن يكون 5 أحرف على الأقل');
      return;
    }
    if (!refundImage) {
      alert('⚠️ صورة الوثيقة إجبارية (PDF غير مقبول لأنه يُحذف بعد فترة)');
      return;
    }
    setRefundBusy(true);
    try {
      const fd = new FormData();
      fd.append('amount', String(amt));
      fd.append('reason', refundReason.trim());
      fd.append('image', refundImage);
      const r = await axios.post(`${API}/daily-operations/${refundOp.id}/refund`, fd, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data',
        },
      });
      // 🆕 Non-admin path returns status='pending_approval'.
      if (r.data?.status === 'pending_approval') {
        alert(
          `📤 ${r.data.message || 'تم إرسال طلب الاسترجاع للموافقة'}\n` +
          `يمكنك متابعة حالة طلبك في "📋 طلباتي" بالقائمة الجانبية.`
        );
      } else {
        alert(`${r.data.message}\nرقم وصل الاسترجاع: ${r.data.refund_operation_no}`);
      }
      setRefundOp(null);
      fetchOperations();
    } catch (e) {
      alert('❌ فشل الاسترجاع: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setRefundBusy(false);
    }
  };

  /**
   * Cross-link helper: scrolls the table to the related row and pulses a
   * highlight ring on it. Works in both directions:
   *   • clicking 🔄 مُسترَجَع on an ORIGINAL row jumps to its REF mirror
   *   • clicking ⛔ استرجاع on a REF row jumps back to the ORIGINAL
   *
   * If the target row is hidden by pagination/filters, we transparently
   * clear filters + quick-search, ask the backend which page the operation
   * lives on, and switch to that page. An effect then picks up the
   * `pendingScrollId` and performs the scroll once the row is in the DOM.
   */
  const scrollToRow = (targetId) => {
    const row = document.getElementById(`op-row-${targetId}`);
    if (!row) return false;
    row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    row.classList.add('ring-4', 'ring-amber-400', 'ring-offset-2');
    setTimeout(() => row.classList.remove('ring-4', 'ring-amber-400', 'ring-offset-2'), 2200);
    return true;
  };

  const jumpToRelatedOperation = async (targetId) => {
    if (!targetId) {
      alert('⚠️ لم يتم العثور على رقم العملية المرتبطة.');
      return;
    }

    // Fast path: the row is already on screen.
    if (scrollToRow(targetId)) return;

    // Slow path: ask the backend which page contains this operation, clear
    // filters + quick-search so nothing hides the row, then switch pages.
    try {
      const r = await axios.get(`${API}/daily-operations/${targetId}/locate`, {
        params: { page_size: pageSize },
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      const targetPage = r.data?.page || 1;

      // Wipe everything that could mask the row.
      setQuickSearch('');
      setActiveFilters({});
      setFilters({
        agency_id: 'all',
        client_name: '',
        service_name: '',
        service_type: 'all',
        status: 'all',
        start_date: '',
        end_date: '',
        min_amount: '',
        max_amount: '',
        season: 'all',  // 'all' so the located op is visible regardless of season
      });
      setPendingScrollId(targetId);

      if (page !== targetPage) {
        setPage(targetPage);
      } else {
        // Same page but row currently hidden (filters) — force a refetch.
        fetchOperations({});
      }
    } catch (e) {
      alert(
        '⚠️ تعذّر تحديد موقع العملية المرتبطة. ربما تم حذفها أو أنها خارج صلاحياتك.'
      );
    }
  };

  // When operations refresh (after a page change triggered by
  // jumpToRelatedOperation), scroll to the pending row exactly once.
  useEffect(() => {
    if (!pendingScrollId) return;
    // Wait a tick so the new rows are committed to the DOM.
    const t = setTimeout(() => {
      const ok = scrollToRow(pendingScrollId);
      if (!ok) {
        alert('⚠️ العملية المرتبطة غير ظاهرة حالياً — حاول إزالة البحث/الفلاتر يدوياً.');
      }
      setPendingScrollId(null);
    }, 150);
    return () => clearTimeout(t);
  }, [operations, pendingScrollId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required document image for new operations
    if (!editingOperation && !formData.document_image) {
      alert('❌ صورة الوثيقة مطلوبة. يرجى رفع صورة للوثيقة قبل حفظ العملية.');
      return;
    }
    
    // Validate custom price for variable services
    if (selectedService && !selectedService.is_fixed_price && (!formData.base_price || parseFloat(formData.base_price) <= 0)) {
      alert('يرجى إدخال سعر صحيح للخدمة المتغيرة');
      return;
    }
    
    try {
      if (editingOperation) {
        // UPDATE existing operation (without image for now)
        console.log('=== UPDATING OPERATION ===');
        console.log('Operation ID:', editingOperation.id);
        
        const submitData = {
          service_id: formData.service_id,
          client_id: formData.client_id,
          base_price: formData.base_price ? parseFloat(formData.base_price) : null,
          discount_amount: formData.discount_amount,
          discount_reason: formData.discount_reason,
          notes: formData.notes,
          // NEW: Payment data
          payment_type: formData.payment_type,
          first_payment_amount: formData.first_payment_amount ? parseFloat(formData.first_payment_amount) : null,
          first_payment_date: formData.first_payment_date || null,
          second_payment_amount: formData.second_payment_amount ? parseFloat(formData.second_payment_amount) : null,
          second_payment_date: formData.second_payment_date || null,
          third_payment_amount: formData.third_payment_amount ? parseFloat(formData.third_payment_amount) : null,
          third_payment_date: formData.third_payment_date || null,
          fourth_payment_amount: formData.fourth_payment_amount ? parseFloat(formData.fourth_payment_amount) : null,
          fourth_payment_date: formData.fourth_payment_date || null,
          // 🆕 Supplier metadata on Edit so the operation can be re-tagged.
          service_origin: formData.service_origin || 'internal',
          supplier_id: formData.service_origin === 'supplier' ? (formData.supplier_id || null) : null,
          supplier_service_id: formData.service_origin === 'supplier' ? (formData.supplier_service_id || null) : null,
          supplier_cost: formData.service_origin === 'supplier' && formData.supplier_cost ? parseFloat(formData.supplier_cost) : null,
        };
        
        console.log('Update data:', submitData);
        
        await axios.put(`${API}/daily-operations/${editingOperation.id}`, submitData, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });

        // 🆕 If the operation is now tagged with a supplier (newly assigned
        // or unchanged), ensure the supplier ledger has the corresponding
        // SALE_DUE row. The backend hook is idempotent — re-running it
        // on the same (operation_id, supplier_id) pair is a no-op.
        if (formData.service_origin === 'supplier' && formData.supplier_id) {
          const cost = parseFloat(formData.supplier_cost || 0);
          const sell = parseFloat(formData.base_price || 0);
          if (cost > 0 && sell > 0) {
            try {
              await axios.post(`${API}/suppliers/_internal/sale-hook`, {
                supplier_id: formData.supplier_id,
                supplier_service_id: formData.supplier_service_id || null,
                operation_id: editingOperation.id,
                operation_type: 'daily_operation',
                sell_price: sell,
                cost_price: cost,
                client_name: clients.find(c => c.id === formData.client_id)?.name,
              }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              });
            } catch (hookErr) {
              console.warn('supplier sale-hook (edit) failed:', hookErr.response?.data || hookErr.message);
            }
          }
        }
        
        console.log('=== UPDATE SUCCESS ===');
        alert('✅ تم تحديث العملية بنجاح');
      } else {
        // CREATE new operation with image
        console.log('=== CREATING OPERATION WITH IMAGE ===');
        
        // Step 1: Create operation first
        const operationData = {
          service_id: formData.service_id,
          client_id: formData.client_id,
          base_price: formData.base_price ? parseFloat(formData.base_price) : null,
          discount_amount: formData.discount_amount,
          discount_reason: formData.discount_reason,
          notes: formData.notes,
          // NEW: Payment data
          payment_type: formData.payment_type,
          first_payment_amount: formData.first_payment_amount ? parseFloat(formData.first_payment_amount) : null,
          first_payment_date: formData.first_payment_date || null,
          second_payment_amount: formData.second_payment_amount ? parseFloat(formData.second_payment_amount) : null,
          second_payment_date: formData.second_payment_date || null,
          third_payment_amount: formData.third_payment_amount ? parseFloat(formData.third_payment_amount) : null,
          third_payment_date: formData.third_payment_date || null,
          fourth_payment_amount: formData.fourth_payment_amount ? parseFloat(formData.fourth_payment_amount) : null,
          fourth_payment_date: formData.fourth_payment_date || null
        };
        
        console.log('Create operation data:', operationData);
        
        const response = await createOperationWithDuplicateGuard(
          operationData,
          formData.document_image,
        );
        if (!response) {
          // 409 duplicate — modal is now open; bail out of the success path.
          return;
        }
        
        const newOperationId = response.data.id; // Back to standard response
        console.log('Operation created with ID:', newOperationId);
        
        // Step 2: Upload image to the created operation
        if (formData.document_image) {
          console.log('=== UPLOADING IMAGE TO OPERATION ===');
          const imageFormData = new FormData();
          imageFormData.append('image', formData.document_image);
          
          await axios.post(`${API}/daily-operations/${newOperationId}/upload-image`, imageFormData, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'multipart/form-data'
            }
          });
          
          console.log('=== IMAGE UPLOADED SUCCESSFULLY ===');
        }

        // 🆕 If this operation sold a SUPPLIER service, register a SALE_DUE
        // line in the supplier ledger. This runs AFTER the operation exists
        // so we can pass its real id. Failure here doesn't roll back the
        // operation — staff can re-link from the supplier page if needed.
        if (formData.service_origin === 'supplier' && formData.supplier_id) {
          const cost = parseFloat(formData.supplier_cost || 0);
          const sell = parseFloat(formData.base_price || 0);
          if (cost > 0 && sell > 0) {
            try {
              await axios.post(`${API}/suppliers/_internal/sale-hook`, {
                supplier_id: formData.supplier_id,
                supplier_service_id: formData.supplier_service_id || null,
                operation_id: newOperationId,
                operation_type: 'daily_operation',
                sell_price: sell,
                cost_price: cost,
                client_name: clients.find(c => c.id === formData.client_id)?.name,
              }, {
                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
              });
            } catch (hookErr) {
              console.warn('supplier sale-hook failed:', hookErr.response?.data || hookErr.message);
              alert('⚠️ تم إنشاء العملية لكن فشل ربطها بالمورد. راجع كشف حساب المورد لاحقاً.');
            }
          }
        }
        
        console.log('=== CREATE WITH IMAGE SUCCESS ===');
        alert('✅ تم إنشاء العملية مع صورة الوثيقة بنجاح');
      }
      
      // Reset form and close dialog
      setShowAddDialog(false);
      setEditingOperation(null);
      setSelectedService(null);
      setFormData({
        service_id: '',
        client_id: '',
        base_price: '',
        discount_amount: 0,
        discount_reason: '',
        notes: '',
        document_image: null,
        // Reset payment fields
        payment_type: 'full',
        first_payment_amount: '',
        first_payment_date: new Date().toISOString().split('T')[0],
        second_payment_amount: '',
        second_payment_date: '',
        third_payment_amount: '',
        third_payment_date: '',
        fourth_payment_amount: '',
        fourth_payment_date: '',
        // 🆕 supplier reset
        service_origin: 'internal',
        supplier_id: '',
        supplier_service_id: '',
        supplier_cost: '',
      });
      fetchOperations();
    } catch (error) {
      console.error('=== OPERATION SUBMIT ERROR ===');
      console.error('Error submitting operation:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لتعديل هذه العملية. اتصل بالمحاسب للمساعدة.');
      } else {
        const action = editingOperation ? 'تحديث' : 'إنشاء';
        alert(`خطأ في ${action} العملية: ` + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handleApprove = async (operationId) => {
    try {
      // 🤝 Subagent flow — if the operation is from a B2B partner agency,
      // open the supplier-picker dialog instead of approving directly.
      const op = operations.find(o => o.id === operationId);
      const agency = agencies.find(a => a.id === op?.agency_id);
      if (op && agency?.is_subagent) {
        setSubagentApprovalTarget({
          operationId,
          itemLabel: `عملية ${op.operation_no || ''}`,
          agencyName: agency?.name,
          sellPrice: op.final_price || op.base_price,
        });
        return;
      }
      console.log('=== APPROVING OPERATION ===');
      console.log('Operation ID:', operationId);
      console.log('API endpoint:', `${API}/daily-operations/${operationId}/approve`);
      console.log('Token:', localStorage.getItem('token') ? 'Present' : 'Missing');
      
      const response = await axios.put(`${API}/daily-operations/${operationId}/approve`, {}, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      console.log('Approval response:', response.data);
      console.log('=== APPROVAL SUCCESS ===');
      
      // Show success message
      alert('✅ تم اعتماد العملية بنجاح');
      
      // Refresh operations list
      fetchOperations();
    } catch (error) {
      console.error('=== APPROVAL ERROR ===');
      console.error('Error approving operation:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      alert('خطأ في اعتماد العملية: ' + (error.response?.data?.detail || error.message));
    }
  };

  // 🤝 Subagent approval dialog state + handler
  const [subagentApprovalTarget, setSubagentApprovalTarget] = useState(null);
  const [subagentApprovalBusy, setSubagentApprovalBusy] = useState(false);
  const handleSubagentApprovalConfirm = async (payload) => {
    if (!subagentApprovalTarget) return;
    setSubagentApprovalBusy(true);
    try {
      await axios.put(
        `${API}/daily-operations/${subagentApprovalTarget.operationId}/approve`,
        payload,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } },
      );
      setSubagentApprovalTarget(null);
      alert('✅ تم اعتماد العملية بنجاح');
      fetchOperations();
    } catch (e) {
      alert('❌ فشل الاعتماد: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubagentApprovalBusy(false);
    }
  };

  const handleReject = async (operationId) => {
    const reason = await promptAsync('سبب الرفض:', '', {
      title: 'رفض العملية',
      placeholder: 'اكتب سبب الرفض هنا...',
      multiline: true,
    });
    if (reason) {
      try {
        console.log('=== REJECTING OPERATION ===');
        console.log('Operation ID:', operationId);
        console.log('Rejection reason:', reason);
        console.log('API endpoint:', `${API}/daily-operations/${operationId}/reject`);
        
        const response = await axios.put(`${API}/daily-operations/${operationId}/reject`, 
          { rejection_reason: reason }, 
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        
        console.log('Rejection response:', response.data);
        console.log('=== REJECTION SUCCESS ===');
        
        // Show success message
        alert('❌ تم رفض العملية بنجاح');
        
        // Refresh operations list
        fetchOperations();
      } catch (error) {
        console.error('=== REJECTION ERROR ===');
        console.error('Error rejecting operation:', error);
        console.error('Error response:', error.response?.data);
        console.error('Error status:', error.response?.status);
        alert('خطأ في رفض العملية: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const handlePrintReceipt = async (operationId, operationNo) => {
    // Get full operation details first
    const operation = operations.find(op => op.id === operationId);
    if (!operation) {
      alert('لم يتم العثور على تفاصيل العملية');
      return;
    }

    // Set the operation for print preview
    setSelectedOperationForPrint(operation);
    
    try {
      // Get REAL payment status for this operation
      console.log('=== FETCHING REAL PAYMENT STATUS FOR RECEIPT ===');
      console.log('Operation ID:', operationId);
      
      const paymentStatus = await fetchOperationPaymentStatus(operationId);
      console.log('Payment status:', paymentStatus);
      
      // Get actual payments for payment method detection
      const paymentsResponse = await axios.get(`${API}/daily-operations/${operationId}/payments`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      const payments = paymentsResponse.data;
      console.log('Payments:', payments);
      
      // Determine payment method from actual payments
      let paymentMethod = 'نقدي'; // default
      if (payments.length > 0) {
        const lastPayment = payments[0]; // Most recent payment
        switch (lastPayment.method) {
          case 'cash':
            paymentMethod = 'نقدي';
            break;
          case 'check':
            paymentMethod = 'شيك';
            break;
          case 'bank_transfer':
            paymentMethod = 'تحويل بنكي';
            break;
          case 'credit_card':
            paymentMethod = 'بطاقة ائتمان';
            break;
          default:
            paymentMethod = 'نقدي';
        }
      }
      
      // Determine payment status in Arabic
      let arabicPaymentStatus;
      switch (paymentStatus?.payment_status) {
        case 'fully_paid':
          arabicPaymentStatus = 'مدفوع كاملاً';
          break;
        case 'partially_paid':
          arabicPaymentStatus = 'دفعة جزئية';
          break;
        case 'unpaid':
          arabicPaymentStatus = 'غير مدفوع';
          break;
        default:
          arabicPaymentStatus = 'غير محدد';
      }
      
      // Initialize print details with REAL payment data
      setPrintDetails({
        paymentType: paymentMethod,
        amountPaid: paymentStatus?.total_paid || 0,
        remainingAmount: paymentStatus?.remaining_amount || operation.final_price,
        paymentStatus: arabicPaymentStatus
      });
      
      console.log('=== PRINT DETAILS SET ===');
      console.log('Payment Type:', paymentMethod);
      console.log('Amount Paid:', paymentStatus?.total_paid || 0);
      console.log('Remaining:', paymentStatus?.remaining_amount || operation.final_price);
      console.log('Status:', arabicPaymentStatus);
      
      // Show preview modal ONLY after payment data is fetched and set
      setShowPrintPreview(true);
      
    } catch (error) {
      console.error('Error fetching payment status for receipt:', error);
      
      // Fallback to default values if payment fetch fails
      setPrintDetails({
        paymentType: 'نقدي',
        amountPaid: 0,
        remainingAmount: operation.final_price,
        paymentStatus: 'غير مدفوع'
      });
      
      alert('⚠️ تعذر جلب بيانات المدفوعات، سيتم استخدام القيم الافتراضية');
      
      // Show preview modal even with fallback data
      setShowPrintPreview(true);
    }
  };

  const handleConfirmPrint = () => {
    if (!selectedOperationForPrint) return;
    try {
      askPdfOrXlsx(`${API}/daily-operations/${selectedOperationForPrint.id}/print`);
      setShowPrintPreview(false);
      setSelectedOperationForPrint(null);
    } catch (error) {
      console.error('Error opening receipt:', error);
      alert('❌ خطأ في فتح الوصل: ' + error.message);
    }
  };
  // NEW: Permission functions for enhanced approval workflow
  const canEditOperation = (operation) => {
    const userRole = user?.role;
    const operationStatus = operation.status;

    if (userRole === 'agency_staff') {
      // Staff can edit if not approved
      return operationStatus !== 'معتمد';
    } else if (userRole === 'general_accountant' || userRole === 'super_admin') {
      // Accountants and Super Admin can edit any operation
      return true;
    }
    return false;
  };

  const canDeleteOperation = (operation) => {
    const userRole = user?.role;
    const operationStatus = operation.status;

    if (userRole === 'agency_staff') {
      // Staff CANNOT delete operations (removed permission)
      return false;
    } else if (userRole === 'general_accountant' || userRole === 'super_admin') {
      // Only Accountants and Super Admin can delete operations
      return true;
    }
    return false;
  };

  // NEW: Edit operation handler
  const handleEditOperation = async (operation) => {
    // 🔒 Linked mirror rows (TKT- / TRIP-) are read-only here. The
    // backend now returns 423 if anyone tries — show a clear toast
    // and short-circuit before opening the edit dialog.
    const opNo = operation.operation_no || '';
    if (opNo.startsWith('TKT-') || opNo.startsWith('TRIP-')) {
      const source = opNo.startsWith('TKT-') ? 'تذكرة الطيران' : 'الرحلة المنظمة';
      alert(
        `⛔ هذه العملية مرتبطة بـ${source}.\n\n` +
        `لا يمكن تعديل السعر أو التخفيض أو العميل من شاشة العمليات ` +
        `اليومية لمنع الخلط بين السجلات.\n\n` +
        `يرجى الرجوع إلى شاشة ${source} الأصلية لإجراء التعديل، ` +
        `وسيُحدَّث السجل المحاسبي تلقائياً.`
      );
      return;
    }
    // Get actual payments for this operation
    try {
      const token = localStorage.getItem('token');
      const paymentsResponse = await axios.get(`${API}/daily-operations/${operation.id}/payments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const payments = paymentsResponse.data.payments || [];
      setCurrentOperationPayments(payments);
      
      // Populate form with operation data and actual payments
      const paymentData = {
        first_payment_amount: '',
        first_payment_date: new Date().toISOString().split('T')[0],
        second_payment_amount: '',
        second_payment_date: '',
        third_payment_amount: '',
        third_payment_date: '',
        fourth_payment_amount: '',
        fourth_payment_date: ''
      };
      
      // Fill payment data from actual payments
      payments.forEach(payment => {
        const paymentNum = payment.payment_number;
        if (paymentNum >= 1 && paymentNum <= 4) {
          const paymentDate = payment.payment_date ? new Date(payment.payment_date).toISOString().split('T')[0] : '';
          
          if (paymentNum === 1) {
            paymentData.first_payment_amount = payment.amount?.toString() || '';
            paymentData.first_payment_date = paymentDate;
          } else if (paymentNum === 2) {
            paymentData.second_payment_amount = payment.amount?.toString() || '';
            paymentData.second_payment_date = paymentDate;
          } else if (paymentNum === 3) {
            paymentData.third_payment_amount = payment.amount?.toString() || '';
            paymentData.third_payment_date = paymentDate;
          } else if (paymentNum === 4) {
            paymentData.fourth_payment_amount = payment.amount?.toString() || '';
            paymentData.fourth_payment_date = paymentDate;
          }
        }
      });
      
      setFormData({
        service_id: operation.service_id,
        client_id: operation.client_id,
        base_price: operation.base_price.toString(),
        discount_amount: operation.discount_amount || 0,
        discount_reason: operation.discount_reason || '',
        notes: operation.notes || '',
        document_image: null, // Cannot edit existing image
        payment_type: operation.payment_type || (payments.length > 1 ? 'installment' : 'full'),
        // 🆕 Carry supplier metadata into Edit so the picker is pre-filled
        service_origin: operation.supplier_id ? 'supplier' : 'internal',
        supplier_id: operation.supplier_id || '',
        supplier_service_id: operation.supplier_service_id || '',
        supplier_cost: operation.supplier_cost != null ? String(operation.supplier_cost) : '',
        ...paymentData
      });
      // Pre-load this supplier's services so the "خدمة المورد" dropdown
      // is populated immediately when the modal opens in Edit mode.
      if (operation.supplier_id) {
        try {
          const r = await axios.get(`${API}/suppliers/${operation.supplier_id}/services?active_only=true`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setSupplierServices(r.data || []);
        } catch { /* ignore */ }
      } else {
        setSupplierServices([]);
      }
      
    } catch (error) {
      console.error('Error fetching operation payments:', error);
      
      // Fallback to basic operation data if payments fetch fails
      setFormData({
        service_id: operation.service_id,
        client_id: operation.client_id,
        base_price: operation.base_price.toString(),
        discount_amount: operation.discount_amount || 0,
        discount_reason: operation.discount_reason || '',
        notes: operation.notes || '',
        document_image: null,
        payment_type: operation.payment_type || 'full',
        // 🆕 Same supplier metadata in the error-fallback branch.
        service_origin: operation.supplier_id ? 'supplier' : 'internal',
        supplier_id: operation.supplier_id || '',
        supplier_service_id: operation.supplier_service_id || '',
        supplier_cost: operation.supplier_cost != null ? String(operation.supplier_cost) : '',
        first_payment_amount: '',
        first_payment_date: new Date().toISOString().split('T')[0],
        second_payment_amount: '',
        second_payment_date: '',
        third_payment_amount: '',
        third_payment_date: '',
        fourth_payment_amount: '',
        fourth_payment_date: ''
      });
    }

    // Set the service for price management
    const service = services.find(s => s.id === operation.service_id);
    setSelectedService(service);

    // Set editing mode
    setEditingOperation(operation);
    setShowAddDialog(true);
  };

  // NEW: Add next payment function
  const addNextPayment = async (operation, paymentNumber, amount, date) => {
    if (!amount || amount <= 0) {
      alert('يرجى إدخال مبلغ صحيح');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/daily-operations/${operation.id}/add-payment`, {
        payment_number: paymentNumber,
        amount: parseFloat(amount),
        payment_date: date,
        notes: `دفعة ${paymentNumber}`
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert(`✅ تم إضافة الدفعة ${paymentNumber} بنجاح`);
      fetchOperations();
      setShowAddDialog(false);
    } catch (error) {
      console.error('Error adding payment:', error);
      alert('❌ فشل في إضافة الدفعة: ' + (error.response?.data?.detail || error.message));
    }
  };

  // NEW: Delete operation handler  
  const handleDeleteOperation = async (operationId) => {
    const operation = operations.find(op => op.id === operationId);
    
    if (!operation) {
      alert('لم يتم العثور على العملية');
      return;
    }

    // Show confirmation with status-specific message
    let confirmMessage = 'هل أنت متأكد من حذف هذه العملية؟';
    if (operation.status === 'معتمد') {
      confirmMessage = '⚠️ تحذير: هذه عملية معتمدة! هل أنت متأكد من حذفها؟ سيتم تسجيل هذا الإجراء لأغراض التدقيق.';
    }
    // 🆕 Cascade warning when the operation is a TKT-/TRIP- mirror so
    // the user knows the source ticket / organized-trip will also be
    // removed (along with payments, supplier ledger, financial entries).
    const opNo = operation.operation_no || '';
    if (opNo.startsWith('TKT-')) {
      confirmMessage += '\n\n🔗 هذه العملية مرتبطة بتذكرة — سيتم حذف التذكرة من تبويبة التذاكر تلقائياً مع كل آثارها (دفعات + قيد دفتر المورد + قيد التتبع المالي).';
    } else if (opNo.startsWith('TRIP-')) {
      confirmMessage += '\n\n🔗 هذه العملية مرتبطة برحلة منظمة — سيتم حذف الرحلة من تبويبة الرحلات المنظمة تلقائياً مع كل دفعاتها وتعديلاتها وقيودها المالية.';
    }

    const ok = await confirmAsync(confirmMessage, {
      title: 'حذف العملية',
      confirmLabel: 'نعم، احذف',
      cancelLabel: 'إلغاء',
      danger: true,
    });
    if (!ok) return;
    try {
      console.log('=== DELETING OPERATION ===');
      console.log('Operation ID:', operationId);
      console.log('Operation Status:', operation.status);

      const resp = await axios.delete(`${API}/daily-operations/${operationId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      console.log('=== DELETION SUCCESS ===');
      const r = resp?.data || {};
      // Friendly summary when the cascade ran (ticket/trip mirror).
      if (r.ticket_id || r.trip_id) {
        alert(
          `✅ تم حذف العملية وكل آثارها بنجاح\n` +
          (r.ticket_id ? `• التذكرة المرتبطة: حُذفت\n` : '') +
          (r.trip_id ? `• الرحلة المنظمة المرتبطة: حُذفت\n` : '') +
          `• الدفعات: ${r.payments_deleted || 0}\n` +
          (r.trip_payments_deleted ? `• دفعات الرحلة: ${r.trip_payments_deleted}\n` : '') +
          `• قيد دفتر المورد: ${r.supplier_ledger_deleted || 0}\n` +
          `• قيد التتبع المالي: ${r.financial_entries_deleted || 0}`
        );
      } else {
        alert('✅ تم حذف العملية بنجاح');
      }

      // Refresh operations list
      fetchOperations();
    } catch (error) {
      console.error('=== DELETION ERROR ===');
      console.error('Error deleting operation:', error);
      console.error('Error response:', error.response?.data);

      // 🆕 Map the 409 FINANCIAL_LOCK so the user knows the cash was
      // already moved and how to undo it.
      const detail = error.response?.data?.detail;
      if (error.response?.status === 409 && typeof detail === 'object' && detail?.code === 'FINANCIAL_LOCK') {
        alert(detail.message || '⛔ لا يمكن الحذف: العملية مرتبطة بدفعة نقدية مغلقة.');
      } else if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لحذف هذه العملية. اتصل بالمحاسب للمساعدة.');
      } else {
        alert('خطأ في حذف العملية: ' + (typeof detail === 'string' ? detail : error.message));
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      'مسودة': { color: 'bg-gray-100 text-gray-800', text: t('draft') },
      'في انتظار الموافقة': { color: 'bg-yellow-100 text-yellow-800', text: t('pendingApproval') },
      'معتمد': { color: 'bg-green-100 text-green-800', text: t('operationApproved') },
      'مرفوض': { color: 'bg-red-100 text-red-800', text: t('operationRejected') }
    };
    
    const statusInfo = statusMap[status] || { color: 'bg-gray-100 text-gray-800', text: status };
    return (
      <Badge className={statusInfo.color}>
        {statusInfo.text}
      </Badge>
    );
  };
  // Image management functions
  const handleImageUpload = async () => {
    if (!selectedImage || !selectedOperationForImage) return;

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('image', selectedImage);

      await axios.post(`${API}/daily-operations/${selectedOperationForImage.id}/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'multipart/form-data'
        }
      });

      alert('✅ تم رفع صورة الوثيقة بنجاح');
      setShowImageDialog(false);
      setSelectedImage(null);
      setSelectedOperationForImage(null);
      fetchOperations();
    } catch (error) {
      console.error('Error uploading image:', error);
      if (error.response?.status === 400) {
        alert('❌ خطأ في الصورة: ' + (error.response?.data?.detail || error.message));
      } else if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لرفع صورة لهذه العملية');
      } else {
        alert('❌ فشل في رفع الصورة: ' + (error.response?.data?.detail || error.message));
      }
    } finally {
      setUploadingImage(false);
    }
  };

  const handleImageRemove = async (operationId) => {
    if (!await confirmAsync('هل أنت متأكد من حذف صورة الوثيقة؟', {
      title: 'حذف الصورة',
      danger: true,
      confirmLabel: 'نعم، احذف',
    })) return;

    try {
      await axios.delete(`${API}/daily-operations/${operationId}/remove-image`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });

      alert('✅ تم حذف صورة الوثيقة بنجاح');
      fetchOperations();
    } catch (error) {
      console.error('Error removing image:', error);
      if (error.response?.status === 403) {
        alert('❌ ليس لديك صلاحية لحذف صورة هذه العملية');
      } else {
        alert('❌ فشل في حذف الصورة: ' + (error.response?.data?.detail || error.message));
      }
    }
  };

  const openImageDialog = (operation) => {
    setSelectedOperationForImage(operation);
    setSelectedImage(null);
    setShowImageDialog(true);
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('يرجى اختيار ملف صورة');
        return;
      }
      
      // Validate file size (2MB)
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        alert('حجم الصورة يجب أن يكون أقل من 2 ميجابايت');
        return;
      }

      // Check file extension
      const allowedExtensions = ['jpg', 'jpeg', 'png'];
      const fileExtension = file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        alert('نوع الملف غير مدعوم. يرجى اختيار صورة JPG أو PNG');
        return;
      }

      setSelectedImage(file);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-brand-600 mx-auto"></div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" dir="rtl">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">{t('dailyOperations')}</h1>
        <div className="flex items-center space-x-3">
          <Button 
            onClick={() => setShowFilters(!showFilters)} 
            variant="outline"
            className="bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
          >
            <Search className="h-4 w-4 ml-2" />
            🔍 فلتر متقدم
          </Button>
          <Button onClick={() => setShowAddDialog(true)} className="bg-brand-600 hover:bg-brand-700">
            <Plus className="h-4 w-4 ml-2" />
            {t('addOperation')}
          </Button>
        </div>
      </div>

      {/* Always-visible quick search — searches by operation number, client
          name and service name simultaneously. Operations are sorted newest-
          first server-side so most recent entries appear here automatically. */}
      <Card className="border-brand-200 shadow-sm">
        <CardContent className="p-3">
          <div className="flex items-center gap-3">
            <Search className="h-5 w-5 text-brand-600 shrink-0" />
            <Input
              type="text"
              value={quickSearch}
              onChange={(e) => setQuickSearch(e.target.value)}
              placeholder="🔍 بحث سريع: رقم الوصل، اسم العميل، الخدمة..."
              className="flex-1 bg-white text-right border-brand-200 focus-visible:ring-brand-400"
              data-testid="ops-quick-search"
            />
            {quickSearch && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setQuickSearch('')}
                className="text-gray-500 hover:text-gray-700"
                data-testid="ops-quick-search-clear"
              >
                ✕ مسح
              </Button>
            )}
            <span className="text-xs text-gray-500 whitespace-nowrap">
              {operations.length} عملية
              <span className="text-green-600 mr-2">⏱️ تحديث تلقائي</span>
            </span>
          </div>
        </CardContent>
      </Card>

      {/* NEW: Image Statistics Alert for Accountants/Managers */}
      {(user?.role === 'General Accountant' || user?.role === 'Super Admin') && (
        (() => {
          const operationsWithImages = operations.filter(op => op.document_image_url).length;
          const operationsWithoutImages = operations.filter(op => !op.document_image_url).length;
          const totalOperations = totalCount || operations.length;
          
          return (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Total Operations */}
              <div className="bg-brand-50 border border-brand-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-brand-100 rounded-lg ml-3">
                    <span className="text-brand-600 text-lg">📊</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-brand-600">إجمالي العمليات</p>
                    <p className="text-2xl font-bold text-brand-900">{totalOperations}</p>
                  </div>
                </div>
              </div>

              {/* Operations with Images */}
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <div className="flex items-center">
                  <div className="p-2 bg-green-100 rounded-lg ml-3">
                    <span className="text-green-600 text-lg">📷</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-green-600">بوثائق مرفقة</p>
                    <p className="text-2xl font-bold text-green-900">
                      {operationsWithImages}
                      <span className="text-sm font-normal text-green-600 mr-1">
                        ({totalOperations > 0 ? Math.round((operationsWithImages / totalOperations) * 100) : 0}%)
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Operations without Images - Alert */}
              <div className={`${operationsWithoutImages > 0 ? 'bg-red-50 border-red-200' : 'bg-gray-50 border-gray-200'} border rounded-lg p-4`}>
                <div className="flex items-center">
                  <div className={`p-2 ${operationsWithoutImages > 0 ? 'bg-red-100' : 'bg-gray-100'} rounded-lg ml-3`}>
                    <span className={`${operationsWithoutImages > 0 ? 'text-red-600' : 'text-gray-600'} text-lg`}>
                      {operationsWithoutImages > 0 ? '⚠️' : '✅'}
                    </span>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${operationsWithoutImages > 0 ? 'text-red-600' : 'text-gray-600'}`}>
                      بدون وثائق
                    </p>
                    <p className={`text-2xl font-bold ${operationsWithoutImages > 0 ? 'text-red-900' : 'text-gray-900'}`}>
                      {operationsWithoutImages}
                      {operationsWithoutImages > 0 && (
                        <span className="text-sm font-normal text-red-600 mr-1">
                          تحتاج مراجعة!
                        </span>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* 🆕 Archive banner — visible when viewing a closed season */}
      {activeFilters.season && activeFilters.season !== 'current' && (
        <div
          className="rounded-lg border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-center justify-between gap-4"
          dir="rtl"
          data-testid="season-archive-banner"
        >
          <div className="flex items-center gap-3">
            <span className="text-2xl">🗂️</span>
            <div>
              <div className="font-bold text-amber-900">
                {activeFilters.season === 'all'
                  ? 'عرض كل المواسم (الحالي + الأرشيف)'
                  : `عرض أرشيف الموسم — ${activeFilters.season}`}
              </div>
              <div className="text-xs text-amber-700">
                هذا العرض للقراءة فقط. لن تتمكن من إنشاء عمليات أو تعديل أرصدة على بيانات الأرشيف.
              </div>
            </div>
          </div>
          <button
            type="button"
            className="px-3 py-2 rounded-md bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium"
            onClick={() => {
              const next = { ...filters, season: 'current' };
              setFilters(next);
              setActiveFilters({ ...activeFilters, season: 'current' });
              if (page !== 1) setPage(1);
              else fetchOperations({ ...activeFilters, season: 'current' });
            }}
            data-testid="season-back-to-current-btn"
          >
            🟢 العودة إلى الموسم الحالي
          </button>
        </div>
      )}

      {/* NEW: Advanced Filtering Panel */}
      {showFilters && (
        <Card className="bg-brand-50 border-brand-200">
          <CardHeader>
            <CardTitle className="text-lg text-brand-800 flex items-center">
              <Search className="h-5 w-5 ml-2" />
              🔍 البحث والفلترة المتقدمة
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {/* 🆕 Season Filter — visible for everyone, lets accountants browse closed seasons */}
              <div>
                <Label htmlFor="filter-season" className="text-sm font-medium text-gray-700">
                  📅 الموسم
                </Label>
                <Select
                  value={filters.season || 'current'}
                  onValueChange={(value) => setFilters({ ...filters, season: value })}
                >
                  <SelectTrigger data-testid="filter-season-trigger">
                    <SelectValue placeholder="الموسم الحالي" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableSeasons.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.value === 'current' ? '🟢 ' : s.value === 'all' ? '🌐 ' : '🗂️ '}{s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Agency Filter - for Super Admin and General Accountant */}
              {['super_admin', 'general_accountant'].includes(user?.role) && (
                <div>
                  <Label htmlFor="filter-agency" className="text-sm font-medium text-gray-700">
                    🏢 الوكالة
                  </Label>
                  <Select value={filters.agency_id} onValueChange={(value) => setFilters({...filters, agency_id: value})}>
                    <SelectTrigger>
                      <SelectValue placeholder="جميع الوكالات" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">جميع الوكالات</SelectItem>
                      {agencies.map((agency) => (
                        <SelectItem key={agency.id} value={agency.id}>
                          🏢 {agency.name} - {agency.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              {/* Client Name Search */}
              <div>
                <Label htmlFor="filter-client-name" className="text-sm font-medium text-gray-700">
                  👤 اسم العميل
                </Label>
                <Input
                  id="filter-client-name"
                  placeholder="ابحث بالاسم..."
                  value={filters.client_name}
                  onChange={(e) => setFilters({...filters, client_name: e.target.value})}
                />
              </div>

              {/* Service Name Search */}
              <div>
                <Label htmlFor="filter-service-name" className="text-sm font-medium text-gray-700">
                  🛠️ اسم الخدمة
                </Label>
                <Input
                  id="filter-service-name"
                  placeholder="ابحث بنوع الخدمة..."
                  value={filters.service_name}
                  onChange={(e) => setFilters({...filters, service_name: e.target.value})}
                />
              </div>

              {/* Service Type Filter */}
              <div>
                <Label htmlFor="filter-service-type" className="text-sm font-medium text-gray-700">
                  📋 نوع الخدمة
                </Label>
                <Select value={filters.service_type} onValueChange={(value) => setFilters({...filters, service_type: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="جميع الأنواع" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الأنواع</SelectItem>
                    <SelectItem value="عمرة">🕋 عمرة</SelectItem>
                    <SelectItem value="حج">🕌 حج</SelectItem>
                    <SelectItem value="تذكرة طيران">✈️ تذكرة طيران</SelectItem>
                    <SelectItem value="حجز فندق">🏨 حجز فندق</SelectItem>
                    <SelectItem value="خدمة تأشيرة">📋 خدمة تأشيرة</SelectItem>
                    <SelectItem value="نقل">🚌 نقل</SelectItem>
                    <SelectItem value="أخرى">🔧 أخرى</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Status Filter */}
              <div>
                <Label htmlFor="filter-status" className="text-sm font-medium text-gray-700">
                  📊 حالة الموافقة
                </Label>
                <Select value={filters.status} onValueChange={(value) => setFilters({...filters, status: value})}>
                  <SelectTrigger>
                    <SelectValue placeholder="جميع الحالات" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">جميع الحالات</SelectItem>
                    <SelectItem value="مسودة">📝 مسودة</SelectItem>
                    <SelectItem value="في انتظار الموافقة">⏳ في انتظار الموافقة</SelectItem>
                    <SelectItem value="معتمد">✅ معتمد</SelectItem>
                    <SelectItem value="مرفوض">❌ مرفوض</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Start Date */}
              <div>
                <Label htmlFor="filter-start-date" className="text-sm font-medium text-gray-700">
                  📅 من تاريخ
                </Label>
                <Input
                  id="filter-start-date"
                  type="date"
                  value={filters.start_date}
                  onChange={(e) => setFilters({...filters, start_date: e.target.value})}
                />
              </div>

              {/* End Date */}
              <div>
                <Label htmlFor="filter-end-date" className="text-sm font-medium text-gray-700">
                  📅 إلى تاريخ
                </Label>
                <Input
                  id="filter-end-date"
                  type="date"
                  value={filters.end_date}
                  onChange={(e) => setFilters({...filters, end_date: e.target.value})}
                />
              </div>

              {/* Minimum Amount */}
              <div>
                <Label htmlFor="filter-min-amount" className="text-sm font-medium text-gray-700">
                  💰 أقل مبلغ (دج)
                </Label>
                <Input
                  id="filter-min-amount"
                  type="number"
                  placeholder="0"
                  value={filters.min_amount}
                  onChange={(e) => setFilters({...filters, min_amount: e.target.value})}
                />
              </div>

              {/* Maximum Amount */}
              <div>
                <Label htmlFor="filter-max-amount" className="text-sm font-medium text-gray-700">
                  💰 أعلى مبلغ (دج)
                </Label>
                <Input
                  id="filter-max-amount"
                  type="number"
                  placeholder="999999999"
                  value={filters.max_amount}
                  onChange={(e) => setFilters({...filters, max_amount: e.target.value})}
                />
              </div>

              {/* 🆕 Supplier Filter */}
              <div>
                <Label htmlFor="filter-supplier" className="text-sm font-medium text-gray-700">
                  📦 المورد
                </Label>
                <select
                  id="filter-supplier"
                  value={filters.supplier_id || 'all'}
                  onChange={(e) => setFilters({...filters, supplier_id: e.target.value})}
                  className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm bg-white"
                  data-testid="filter-supplier"
                >
                  <option value="all">— كل العمليات —</option>
                  <option value="__internal__">🏢 داخلية فقط (بدون مورد)</option>
                  {suppliers.map(s => (
                    <option key={s.id} value={s.id}>📦 {s.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Filter Action Buttons */}
            <div className="flex justify-between items-center pt-4 border-t border-brand-200">
              <div className="text-sm text-brand-700">
                📊 عدد العمليات المعروضة: {operations.length}
              </div>
              <div className="flex space-x-2">
                <Button 
                  onClick={clearFilters}
                  variant="outline" 
                  className="text-gray-700 border-gray-300"
                >
                  🗑️ مسح الفلاتر
                </Button>
                <Button 
                  onClick={applyFilters}
                  className="bg-brand-600 hover:bg-brand-700 text-white"
                >
                  🔍 تطبيق الفلتر
                </Button>
              </div>
            </div>

            {/* Quick Filter Examples */}
            <div className="bg-white p-3 rounded-lg border border-brand-200">
              <p className="text-sm font-medium text-brand-800 mb-2">🚀 فلاتر سريعة:</p>
              <div className="flex flex-wrap gap-2">
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setFilters({...filters, start_date: new Date().toISOString().split('T')[0], service_name: 'عمرة'});
                    setTimeout(applyFilters, 100);
                  }}
                  className="text-xs bg-green-50 hover:bg-green-100 text-green-700 border-green-200"
                >
                  🕋 عمرة اليوم
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setFilters({...filters, status: 'في انتظار الموافقة'});
                    setTimeout(applyFilters, 100);
                  }}
                  className="text-xs bg-orange-50 hover:bg-orange-100 text-orange-700 border-orange-200"
                >
                  ⏳ تحتاج موافقة
                </Button>
                <Button 
                  size="sm" 
                  variant="outline"
                  onClick={() => {
                    setFilters({...filters, min_amount: '100000'});
                    setTimeout(applyFilters, 100);
                  }}
                  className="text-xs bg-purple-50 hover:bg-purple-100 text-purple-700 border-purple-200"
                >
                  💰 مبالغ عالية
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="p-6">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="text-right">المنفذ بواسطة</TableHead>
                <TableHead className="text-right">رقم الوصل</TableHead>
                <TableHead className="text-right">التاريخ</TableHead>
                <TableHead className="text-right">العميل</TableHead>
                <TableHead className="text-right">الخدمة</TableHead>
                <TableHead className="text-right">🛏️ نوع الغرفة</TableHead>
                <TableHead className="text-right">📦 المورد</TableHead>
                <TableHead className="text-right">💰 تكلفة المورد</TableHead>
                <TableHead className="text-right">📈 الربح</TableHead>
                <TableHead className="text-right">نوع العملية</TableHead>
                <TableHead className="text-right">المبلغ الإجمالي</TableHead>
                <TableHead className="text-right">المبلغ المدفوع</TableHead>
                <TableHead className="text-right">المبلغ المتبقي</TableHead>
                <TableHead className="text-right">📄 الوثيقة</TableHead>
                <TableHead className="text-right">حالة الدفع</TableHead>
                <TableHead className="text-right">📅 موعد القسط القادم</TableHead>
                <TableHead className="text-right">الإجراءات</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visibleOperations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} className="text-center py-8 text-gray-500">
                    {quickSearch ? `🔍 لا توجد نتائج للبحث "${quickSearch}"` : 'لا توجد عمليات'}
                  </TableCell>
                </TableRow>
              ) : visibleOperations.map((operation) => {
                const isRefund = (operation.operation_no || '').startsWith('REF-');
                return (
                <TableRow
                  key={operation.id}
                  id={`op-row-${operation.id}`}
                  className={
                    isRefund
                      ? 'bg-red-50 hover:bg-red-100 border-r-4 border-r-red-400'
                      : (operation.pending_refund_credit || 0) > 0
                        ? 'bg-rose-50 hover:bg-rose-100'
                        : operation.installment_receipt_id
                          ? 'bg-brand-50 hover:bg-brand-100'
                          : 'hover:bg-gray-50'
                  }
                  data-testid={
                    isRefund
                      ? `op-row-refund-${operation.id}`
                      : (operation.pending_refund_credit || 0) > 0
                        ? `op-row-refund-pending-${operation.id}`
                        : operation.installment_receipt_id
                          ? `op-row-converted-${operation.id}`
                          : undefined
                  }
                >
                  {/* Performed By (First Column) */}
                  <TableCell className="text-right font-medium text-gray-700">
                    {operation.created_by_name || 'غير محدد'}
                  </TableCell>
                  
                  {/* Receipt/Operation Number */}
                  <TableCell className="text-right font-medium text-brand-600">
                    <div className="flex items-center justify-end gap-1 flex-wrap">
                      <span className={isRefund ? 'text-red-700 font-bold' : ''}>{operation.operation_no}</span>
                      {isRefund && (
                        <button
                          type="button"
                          onClick={() => jumpToRelatedOperation(operation.source_operation_id)}
                          className="inline-flex items-center gap-1 bg-red-600 hover:bg-red-700 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full cursor-pointer transition"
                          title={operation.source_operation_no
                            ? `اضغط لرؤية العملية الأصلية ${operation.source_operation_no}`
                            : 'عملية استرجاع'}
                          data-testid={`badge-refund-back-${operation.id}`}
                        >
                          ⛔ استرجاع
                        </button>
                      )}
                      <LifecycleBadge operation={operation} size="xs" />
                      {operation.installment_receipt_id && (
                        <span
                          className="inline-flex items-center gap-1 bg-brand-100 text-brand-800 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                          title="هذه العملية محوّلة إلى نظام الأقساط"
                        >
                          💳 تحت الأقساط
                        </span>
                      )}
                      {(operation.pending_refund_credit || 0) > 0 && (
                        <span
                          className="inline-flex items-center gap-1 bg-rose-100 text-rose-800 text-[10px] font-semibold px-1.5 py-0.5 rounded-full"
                          title={`يجب استرجاع ${(operation.pending_refund_credit || 0).toLocaleString()} دج للعميل`}
                        >
                          ↩️ استرجاع مطلوب
                        </span>
                      )}
                    </div>
                  </TableCell>
                  
                  {/* Date + time + employee — manager wants every row
                      to tell him WHEN it was entered and BY WHOM. */}
                  <TableCell className="text-right">
                    <div>{formatDateWithEnglishNumerals(operation.date)}</div>
                    {operation.created_at && (
                      <div className="text-xs text-gray-500" dir="ltr">
                        🕐 {formatTimeWithEnglishNumerals(operation.created_at)}
                      </div>
                    )}
                    {operation.created_by_name && (
                      <div className="text-xs text-amber-700 mt-0.5">
                        👤 {operation.created_by_name}
                      </div>
                    )}
                  </TableCell>
                  
                  {/* Client */}
                  <TableCell className="text-right">
                    {operation.client_name || clients.find(c => c.id === operation.client_id)?.name || 'غير محدد'}
                  </TableCell>
                  
                  {/* Service */}
                  <TableCell className="text-right">
                    {operation.service_name}
                  </TableCell>

                  {/* 🆕 Room Type — derived from the cascading service label.
                      Hierarchical services persist the label as
                      "Service - Hotel - Room (with meals)" so we extract
                      the room segment for instant program context. */}
                  <TableCell className="text-right" data-testid={`row-roomtype-${operation.id}`}>
                    {(() => {
                      const label = operation.service_name || '';
                      const hasMeals = /\(\s*مع\s*ا?لإ?عاشة\s*\)/.test(label);
                      const clean = label.replace(/\s*\(\s*مع\s*ا?لإ?عاشة\s*\)\s*$/, '');
                      const parts = clean.split(' - ').map(s => s.trim()).filter(Boolean);
                      const room = parts.length >= 3 ? parts[parts.length - 1] : null;
                      if (!room) return <span className="text-gray-300 text-xs">—</span>;
                      const hotel = parts.length >= 3 ? parts[parts.length - 2] : null;
                      return (
                        <span
                          className="inline-block px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 border border-amber-200 text-amber-800"
                          title={hotel ? `الفندق: ${hotel}` : ''}
                        >
                          🛏️ {room}{hasMeals ? ' • مع إعاشة' : ''}
                        </span>
                      );
                    })()}
                  </TableCell>

                  {/* 🆕 Supplier */}
                  <TableCell className="text-right" data-testid={`row-supplier-${operation.id}`}>
                    {operation.supplier_id ? (
                      <span
                        className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs font-semibold"
                        title={operation.supplier_name || operation.supplier_id}
                      >
                        📦 {operation.supplier_name || (suppliers.find(s => s.id === operation.supplier_id)?.name) || '—'}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">— داخلي —</span>
                    )}
                  </TableCell>

                  {/* 🆕 Supplier Cost — transparent to every employee
                      so the team can audit each other. */}
                  <TableCell className="text-right" data-testid={`row-supplier-cost-${operation.id}`}>
                    {operation.supplier_cost != null && operation.supplier_cost > 0 ? (
                      <span className="text-red-700 font-semibold">
                        {Number(operation.supplier_cost).toLocaleString()} دج
                      </span>
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* 🆕 Profit (sell price − supplier cost) */}
                  <TableCell className="text-right" data-testid={`row-profit-${operation.id}`}>
                    {operation.supplier_cost != null && operation.supplier_cost > 0 ? (
                      (() => {
                        const profit = (operation.final_price ?? operation.base_price ?? 0) - operation.supplier_cost;
                        return (
                          <span className={profit >= 0 ? 'text-green-700 font-bold' : 'text-red-700 font-bold'}>
                            {profit >= 0 ? '+' : ''}{Number(profit).toLocaleString()} دج
                          </span>
                        );
                      })()
                    ) : (
                      <span className="text-gray-300 text-xs">—</span>
                    )}
                  </TableCell>

                  {/* Operation Type */}
                  <TableCell className="text-right">
                    {operation.operation_no?.startsWith('INST-') ? (
                      <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs">
                        📋 قسط
                      </span>
                    ) : operation.payment_type === 'flexible_installment' ? (
                      <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs">
                        🌟 تقسيط مريح
                      </span>
                    ) : operation.payment_type === 'installment' ? (
                      <span className="bg-brand-100 text-brand-800 px-2 py-1 rounded-full text-xs">
                        📅 تقسيط
                      </span>
                    ) : (
                      <span className="bg-gray-100 text-gray-800 px-2 py-1 rounded-full text-xs">
                        💰 عادي
                      </span>
                    )}
                  </TableCell>
                  
                  {/* Total Amount */}
                  <TableCell className="text-right font-bold text-brand-600">
                    {(operation.final_price || 0).toLocaleString()} دج
                  </TableCell>
                  
                  {/* Amount Paid - Enhanced Calculation */}
                  <TableCell className="text-right">
                    <div className="font-bold text-green-600">
                      {(() => {
                        // For organized trips, use the total_paid from the operation itself
                        if (operation.payment_type === 'organized_trip' || operation.payment_type === 'trip_payment') {
                          return (operation.total_paid || 0).toLocaleString();
                        }
                        // For other operations, use the payment status calculation
                        const totalPaid = operationPaymentStatuses[operation.id]?.total_paid || operation.total_paid || 0;
                        return totalPaid.toLocaleString();
                      })()} دج
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {(() => {
                        if (operation.payment_type === 'organized_trip') return 'رحلة منظمة';
                        if (operation.payment_type === 'trip_payment') return 'دفعة رحلة';
                        if (operation.payment_type === 'ticket') return 'تذكرة';
                        if (operation.payment_type === 'installment') return 'قسط';
                        return 'عملية عادية';
                      })()}
                    </div>
                  </TableCell>
                  
                  {/* NEW: Remaining Amount with Color Coding */}
                  <TableCell className="text-right">
                    {(() => {
                      // Calculate remaining amount
                      let totalPaid = 0;
                      if (operation.payment_type === 'organized_trip' || operation.payment_type === 'trip_payment') {
                        totalPaid = operation.total_paid || 0;
                      } else {
                        totalPaid = operationPaymentStatuses[operation.id]?.total_paid || operation.total_paid || 0;
                      }
                      const remaining = (operation.final_price || 0) - totalPaid;
                      
                      return (
                        <div className={`font-bold ${
                          remaining <= 0 ? 'text-green-600' : 
                          remaining < (operation.final_price || 0) * 0.5 ? 'text-yellow-600' : 'text-red-600'
                        }`}>
                          {remaining.toLocaleString()} دج
                        </div>
                      );
                    })()}
                  </TableCell>
                  
                  {/* NEW: Document Type with Click to View */}
                  <TableCell className="text-right">
                    <div className="flex items-center justify-center">
                      {operation.document_type === 'pdf' && operation.document_image_url ? (
                        <div 
                          className="flex items-center space-x-1 cursor-pointer hover:bg-red-50 p-2 rounded-lg transition-colors"
                          onClick={() => viewDocument(operation.document_image_url, 'pdf', operation.operation_no)}
                          title="انقر لمشاهدة ملف PDF"
                        >
                          <span className="text-red-600 text-lg">📄</span>
                          <span className="text-xs text-red-800">PDF</span>
                        </div>
                      ) : operation.document_type === 'image' && operation.document_image_url ? (
                        <div 
                          className="flex items-center space-x-1 cursor-pointer hover:bg-brand-50 p-2 rounded-lg transition-colors"
                          onClick={() => viewDocument(operation.document_image_url, 'image', operation.operation_no)}
                          title="انقر لمشاهدة الصورة"
                        >
                          <span className="text-brand-600 text-lg">🖼️</span>
                          <span className="text-xs text-brand-800">صورة</span>
                        </div>
                      ) : operation.document_image_url ? (
                        <div 
                          className="flex items-center space-x-1 cursor-pointer hover:bg-gray-50 p-2 rounded-lg transition-colors"
                          onClick={() => viewDocument(operation.document_image_url, 'unknown', operation.operation_no)}
                          title="انقر لمشاهدة الوثيقة"
                        >
                          <span className="text-gray-600 text-lg">📎</span>
                          <span className="text-xs text-gray-600">وثيقة</span>
                        </div>
                      ) : (
                        <span className="text-xs text-gray-400">لا توجد</span>
                      )}
                    </div>
                  </TableCell>
                  
                  {/* Enhanced Payment Status */}
                  <TableCell className="text-right">
                    <div className="space-y-1">
                      {getStatusBadge(operation.status)}
                      
                      {/* Enhanced Payment Status Badge */}
                      {(() => {
                        let totalPaid = 0;
                        if (operation.payment_type === 'organized_trip' || operation.payment_type === 'trip_payment') {
                          totalPaid = operation.total_paid || 0;
                        } else {
                          totalPaid = operationPaymentStatuses[operation.id]?.total_paid || operation.total_paid || 0;
                        }
                        
                        const finalPrice = operation.final_price || 0;
                        const remaining = finalPrice - totalPaid;
                        
                        if (remaining <= 0) {
                          return <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-medium">✅ مدفوع كاملاً</span>;
                        } else if (totalPaid > 0) {
                          return <span className="bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full text-xs font-medium">🟡 جزئي ({Math.round((totalPaid/finalPrice)*100)}%)</span>;
                        } else {
                          return <span className="bg-red-100 text-red-800 px-2 py-1 rounded-full text-xs font-medium">❌ غير مدفوع</span>;
                        }
                      })()}
                    </div>
                  </TableCell>

                  {/* Next Installment Due Date (only for converted operations) */}
                  <TableCell className="text-right">
                    {operation.installment_receipt_id ? (() => {
                      const info = installmentNextDue[operation.installment_receipt_id];
                      if (!info) {
                        return <span className="text-xs text-gray-400">...</span>;
                      }
                      if (info.fully_paid) {
                        return <span className="bg-green-100 text-green-800 px-2 py-1 rounded text-xs font-semibold">✅ مكتمل</span>;
                      }
                      if (!info.next_due_date) {
                        return <span className="text-xs text-gray-400">—</span>;
                      }
                      const cls = info.is_overdue
                        ? 'bg-red-100 text-red-800 border border-red-300'
                        : 'bg-brand-50 text-brand-800 border border-brand-200';
                      const label = info.is_overdue ? '⚠️ متأخر' : '📅';
                      return (
                        <div className={`inline-flex flex-col items-center px-2 py-1 rounded ${cls}`} title={`قسط ${info.payment_number} - ${(info.amount_due || 0).toLocaleString()} دج`}>
                          <span className="text-xs font-semibold leading-tight">{label}</span>
                          <span className="text-xs leading-tight">{new Date(info.next_due_date).toLocaleDateString('ar-DZ')}</span>
                        </div>
                      );
                    })() : (
                      <span className="text-xs text-gray-300">—</span>
                    )}
                  </TableCell>

                  {/* Actions */}
                  <TableCell className="min-w-[260px]">
                    <div className="flex justify-end flex-wrap gap-1">
                      {/* Print Button */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePrintReceipt(operation.id, operation.operation_no)}
                        className="text-brand-600 hover:text-brand-700 text-xs"
                        title="معاينة وطباعة الوصل"
                        data-testid={`print-op-${operation.id}`}
                      >
                        🖨️
                      </Button>

                      {/* 🆕 Grant Credit Note — fully paid only. Open to ALL roles
                          (employees get pending_approval; admin auto-approve). */}
                      {(Number(operation.amount || 0) - Number(operation.amount_paid || 0)) <= 0.01
                        && Number(operation.amount || 0) > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setCreditNoteFor({
                            id: operation.id,
                            label: operation.operation_no,
                          })}
                          className="text-amber-700 border-amber-400 hover:bg-amber-50 text-xs"
                          title="منح تخفيض استثنائي بعد التسوية الكاملة"
                          data-testid={`grant-cn-op-${operation.id}`}
                        >
                          💸
                        </Button>
                      )}

                      {/* WhatsApp share button — opens wa.me with a 7-day public PDF link */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleShareViaWhatsApp(operation)}
                        className="text-green-600 hover:text-green-700 text-xs"
                        title="إرسال PDF عبر واتساب"
                        data-testid={`share-wa-${operation.id}`}
                      >
                        📤
                      </Button>

                      {/* Refund button — hidden on REF-* rows and on already-refunded operations */}
                      {!isRefund && !operation.refund_operation_no && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openOperationRefund(operation)}
                          className="text-orange-700 border-orange-400 hover:bg-orange-50 text-xs font-bold"
                          title="استرجاع هذه العملية (سينشئ عملية سالبة بنفس المبلغ)"
                          data-testid={`refund-op-${operation.id}`}
                        >
                          💸
                        </Button>
                      )}
                      {operation.refund_operation_no && (
                        <button
                          type="button"
                          onClick={() => jumpToRelatedOperation(operation.refund_operation_id)}
                          className="inline-flex items-center text-[10px] bg-orange-100 hover:bg-orange-200 text-orange-800 border border-orange-300 rounded-full px-2 py-0.5 font-bold whitespace-nowrap cursor-pointer transition"
                          title={`اضغط لرؤية وصل الاسترجاع ${operation.refund_operation_no}${operation.refund_by_name ? ' • ' + operation.refund_by_name : ''}`}
                          data-testid={`badge-refunded-${operation.id}`}
                        >
                          🔄 مُسترَجَع
                        </button>
                      )}

                      {/* Back to Receipt Button for installments */}
                      {operation.operation_no?.includes('INST-') && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            const receiptNo = operation.operation_no.split('-P')[0]; // Remove -P1, -P2 etc
                            alert(`🔍 ابحث عن الوصل ${receiptNo} في نظام الأقساط لإضافة دفعة جديدة`);
                          }}
                          className="text-purple-600 hover:text-purple-700 text-xs"
                          title="العودة للوصل"
                          data-testid={`back-to-receipt-${operation.id}`}
                        >
                          📋
                        </Button>
                      )}

                      {/* Add Payment Button for non-installments */}
                      {!operation.operation_no?.includes('INST-') && (() => {
                        const paymentStatus = operationPaymentStatuses[operation.id];
                        return paymentStatus?.payment_status !== 'fully_paid' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleAddPayment(operation)}
                            className="text-green-600 hover:text-green-700 text-xs"
                          >
                            💰
                          </Button>
                        );
                      })()}

                      {/* Convert remaining to installments */}
                      {!operation.operation_no?.includes('INST-') && !operation.installment_receipt_id && (() => {
                        const ps = operationPaymentStatuses[operation.id];
                        const remaining = ps?.remaining_amount ?? (operation.final_price - (operation.total_paid || 0));
                        return remaining > 0 && ps?.payment_status !== 'fully_paid' && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openConvertDialog(operation, remaining)}
                            className="text-purple-600 hover:text-purple-700 text-xs"
                            title="تحويل المبلغ المتبقي إلى نظام الأقساط"
                            data-testid={`convert-to-installments-${operation.id}`}
                          >
                            💳
                          </Button>
                        );
                      })()}

                      {/* Refund pending credit (price went down after payment) */}
                      {(operation.pending_refund_credit || 0) > 0 && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleProcessRefundCredit(operation)}
                          className="text-rose-600 hover:text-rose-700 text-xs border-rose-300 bg-rose-50 hover:bg-rose-100 font-bold"
                          title={`استرجاع ${(operation.pending_refund_credit || 0).toLocaleString()} دج للعميل`}
                          data-testid={`process-refund-credit-${operation.id}`}
                        >
                          ↩️ {(operation.pending_refund_credit || 0).toLocaleString()}
                        </Button>
                      )}

                      {/* Refresh to current service price */}
                      {!operation.operation_no?.includes('INST-') && !operation.installment_receipt_id && (() => {
                        const ps = operationPaymentStatuses[operation.id];
                        const fullyPaid = ps?.payment_status === 'fully_paid';
                        return !fullyPaid && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleRefreshServicePrice(operation)}
                            className="text-orange-600 hover:text-orange-700 text-xs"
                            title="تحديث سعر هذه العملية إلى السعر الحالي للخدمة"
                            data-testid={`refresh-service-price-${operation.id}`}
                          >
                            🔄
                          </Button>
                        );
                      })()}

                      {/* Edit Button */}
                      {canEditOperation(operation) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEditOperation(operation)}
                          className="text-brand-600 hover:text-brand-700 text-xs"
                        >
                          ✏️
                        </Button>
                      )}

                      {/* Delete Button */}
                      {canDeleteOperation(operation) && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleDeleteOperation(operation.id)}
                          className="text-red-600 hover:text-red-700 text-xs"
                          title="حذف العملية"
                          data-testid={`delete-op-${operation.id}`}
                        >
                          🗑️
                        </Button>
                      )}

                      {/* Approval Buttons */}
                      {canApproveOperations && operation.status === 'في انتظار الموافقة' && (
                        <>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleApprove(operation.id)}
                            className="text-green-600 hover:text-green-700 text-xs"
                          >
                            ✅
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleReject(operation.id)}
                            className="text-red-600 hover:text-red-700 text-xs"
                          >
                            ❌
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>

          {/* Pagination Controls — newest entries always at the top, paged */}
          {(totalCount > 0 || page > 1) && (
            <div
              className="flex flex-col md:flex-row items-center justify-between gap-3 mt-4 px-3 py-2 bg-gray-50 rounded-lg border"
              data-testid="daily-ops-pagination"
            >
              <div className="text-sm text-gray-700" data-testid="daily-ops-page-info">
                {totalCount > 0 ? (
                  <>
                    عرض{' '}
                    <span className="font-bold text-brand-600">
                      {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalCount)}
                    </span>{' '}
                    من إجمالي{' '}
                    <span className="font-bold text-brand-600">{totalCount.toLocaleString()}</span>{' '}
                    عملية · الصفحة{' '}
                    <span className="font-bold">{page}</span> من{' '}
                    <span className="font-bold">{totalPages}</span>
                  </>
                ) : (
                  <>لا توجد نتائج</>
                )}
              </div>

              <div className="flex items-center gap-2 flex-wrap justify-center">
                <label className="text-xs text-gray-600">حجم الصفحة:</label>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  className="border rounded-md px-2 py-1 text-sm bg-white"
                  data-testid="daily-ops-page-size"
                >
                  {[25, 50, 100, 200].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>

                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage(1)}
                  data-testid="daily-ops-page-first"
                  title="الصفحة الأولى"
                >
                  ⏮
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="daily-ops-page-prev"
                >
                  ▶ السابق
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="daily-ops-page-next"
                >
                  التالي ◀
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage(totalPages)}
                  data-testid="daily-ops-page-last"
                  title="الصفحة الأخيرة"
                >
                  ⏭
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Operation Dialog */}
      <Dialog open={showAddDialog} onOpenChange={(open) => {
        setShowAddDialog(open);
        if (!open) {
          setEditingOperation(null);
          setSelectedService(null);
          setFormData({
            service_id: '',
            client_id: '',
            base_price: '',
            discount_amount: 0,
            discount_reason: '',
            notes: '',
            // Reset document fields
            document_image: null,
            document_type: 'image',
            // Reset payment fields
            payment_type: 'full',
            first_payment_amount: '',
            first_payment_date: new Date().toISOString().split('T')[0],
            second_payment_amount: '',
            second_payment_date: '',
            third_payment_amount: '',
            third_payment_date: '',
            fourth_payment_amount: '',
            fourth_payment_date: '',
            flexible_installments: 0,
            installment_period: 'monthly'
          });
        }
      }}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              {editingOperation ? '✏️ تعديل العملية' : '➕ ' + t('addOperation')}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="client_id">{t('clientName')}</Label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <SearchableSelect
                    value={formData.client_id}
                    onChange={(value) => setFormData({ ...formData, client_id: value })}
                    options={clients.map(c => ({
                      value: c.id,
                      label: c.name,
                      hint: c.phone || '',
                      search: `${c.name} ${c.phone || ''} ${c.email || ''}`,
                    }))}
                    placeholder={clients.length === 0 ? "لا توجد عملاء - أضف عميل جديد أولاً" : "اختر العميل"}
                    searchPlaceholder="🔎 ابحث بالاسم أو الهاتف..."
                    emptyText="لا يوجد عميل مطابق. استخدم زر '➕ عميل جديد'"
                    testId="op-client-select"
                  />
                </div>

                {/* Quick Add Client Button */}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    // Close operation dialog first to avoid conflict
                    setShowAddDialog(false);

                    // Small delay then open client dialog
                    setTimeout(() => {
                      setShowAddClientDialog(true);
                    }, 100);
                  }}
                  className="bg-green-50 text-green-700 hover:bg-green-100 border-green-200"
                >
                  ➕ عميل جديد
                </Button>
              </div>

              {clients.length === 0 && (
                <div className="mt-2 p-3 bg-brand-50 border border-brand-200 rounded-lg">
                  <div className="flex items-center text-brand-700">
                    <div className="ml-2">💡</div>
                    <div>
                      <p className="font-medium">مرحباً بك في الوكالة الجديدة!</p>
                      <p className="text-sm">تحتاج لإضافة عملاء أولاً قبل تسجيل العمليات. اضغط "➕ عميل جديد" لإضافة أول عميل.</p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div>
              <Label htmlFor="service_id">{t('serviceName')}</Label>
              <SearchableSelect
                value={formData.service_id}
                onChange={(value) => {
                  // Mimic handleServiceChange behavior: pass through the value
                  // exactly as Select would.
                  handleServiceChange(value);
                }}
                options={services.map(s => ({
                  value: s.id,
                  label: s.name,
                  hint: s.is_fixed_price
                    ? `${(s.base_price || 0).toLocaleString()} دج`
                    : '📝 سعر متغير',
                  search: `${s.name} ${s.description || ''}`,
                }))}
                placeholder="اختر الخدمة"
                searchPlaceholder="🔎 ابحث باسم الخدمة..."
                emptyText="لا توجد خدمة مطابقة"
                testId="op-service-select"
              />
            </div>

            {/* Show price input for variable services */}
            {selectedService && !selectedService.is_fixed_price && (
              <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                <div className="flex items-center mb-2">
                  <Info className="h-4 w-4 text-brand-600 ml-2" />
                  <span className="text-brand-800 font-medium">خدمة بسعر متغير</span>
                </div>
                <div>
                  <Label htmlFor="base_price">السعر النهائي (دج) *</Label>
                  <Input
                    id="base_price"
                    type="number"
                    value={formData.base_price}
                    onChange={(e) => setFormData({...formData, base_price: e.target.value})}
                    placeholder="أدخل السعر للخدمة"
                    required
                    className="mt-1"
                  />
                  <p className="text-sm text-brand-600 mt-1">
                    💡 يمكنك تحديد السعر المناسب لهذه الخدمة
                  </p>
                </div>
              </div>
            )}

            {/* Show price info for fixed services */}
            {selectedService && selectedService.is_fixed_price && (
              <div className="bg-gray-50 p-3 rounded-lg border">
                <div className="flex items-center">
                  <CheckCircle className="h-4 w-4 text-green-600 ml-2" />
                  <span className="text-gray-700">
                    السعر الثابت: <strong>{selectedService.base_price.toLocaleString()} دج</strong>
                  </span>
                </div>
              </div>
            )}

            {/* 🆕 Supplier integration — origin selector + supplier picker.
                Visible in BOTH Add and Edit so a supplier can be assigned
                or changed after creation. Hidden for subagent users — they
                always sell Sanhaja services (no supplier exposure). */}
            {!user?.is_subagent && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 space-y-3" data-testid="service-origin-block">
                <Label className="text-sm font-bold text-yellow-800">مصدر الخدمة</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, service_origin: 'internal', supplier_id: '', supplier_service_id: '', supplier_cost: '' })}
                    className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${formData.service_origin === 'internal' ? 'bg-green-100 border-green-400 text-green-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                    data-testid="origin-internal-btn"
                  >🏢 خدمة صنهاجة</button>
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, service_origin: 'supplier' })}
                    className={`flex-1 px-3 py-2 rounded-md text-sm border transition ${formData.service_origin === 'supplier' ? 'bg-yellow-100 border-yellow-500 text-yellow-800 font-bold' : 'bg-white border-gray-200 text-gray-600'}`}
                    data-testid="origin-supplier-btn"
                  >📦 خدمة مورد</button>
                </div>

                {formData.service_origin === 'supplier' && (
                  <div className="space-y-2 mt-2">
                    <div>
                      <Label className="text-xs">المورد *</Label>
                      <select
                        value={formData.supplier_id}
                        onChange={async (e) => {
                          const sid = e.target.value;
                          setFormData(f => ({ ...f, supplier_id: sid, supplier_service_id: '', supplier_cost: '' }));
                          setSupplierServices([]);
                          if (sid) {
                            try {
                              const r = await axios.get(`${API}/suppliers/${sid}/services?active_only=true`, {
                                headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
                              });
                              setSupplierServices(r.data || []);
                            } catch { /* ignore */ }
                          }
                        }}
                        className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                        data-testid="supplier-select"
                      >
                        <option value="">اختر مورداً...</option>
                        {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                      </select>
                    </div>
                    {formData.supplier_id && (
                      <div>
                        <Label className="text-xs">خدمة المورد (اختياري)</Label>
                        <select
                          value={formData.supplier_service_id}
                          onChange={(e) => {
                            const svcId = e.target.value;
                            const svc = supplierServices.find(x => x.id === svcId);
                            setFormData(f => ({
                              ...f,
                              supplier_service_id: svcId,
                              // Auto-fill the cost from the supplier service catalog
                              supplier_cost: svc?.cost_price != null ? String(svc.cost_price) : f.supplier_cost,
                              // If user hasn't set a sell price yet, suggest one
                              base_price: !f.base_price && svc?.suggested_sell_price != null ? String(svc.suggested_sell_price) : f.base_price,
                            }));
                          }}
                          className="w-full px-3 py-2 rounded-md border border-gray-300 text-sm"
                          data-testid="supplier-service-select"
                        >
                          <option value="">— اختر خدمة لتعبئة التكلفة تلقائياً —</option>
                          {supplierServices.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.name} {s.cost_price != null ? `(تكلفة ${s.cost_price.toLocaleString()})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    {/* 🆕 Transparency mode — supplier cost is visible to
                        every employee so the team can audit each other
                        and keep the books honest. */}
                    <div>
                      <Label className="text-xs">تكلفة المورد (دج) *</Label>
                      <Input
                        type="number"
                        value={formData.supplier_cost}
                        onChange={(e) => setFormData({ ...formData, supplier_cost: e.target.value })}
                        placeholder="ما تدفعه الوكالة للمورد"
                        data-testid="supplier-cost-input"
                      />
                      {formData.supplier_cost && formData.base_price && (
                        <p className="text-xs mt-1 text-green-700">
                          💰 الربح المحسوب: {(parseFloat(formData.base_price || 0) - parseFloat(formData.supplier_cost || 0)).toLocaleString()} دج
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="discount_amount">{t('discountAmount')}</Label>
              <Input
                id="discount_amount"
                type="number"
                value={formData.discount_amount}
                onChange={(e) => setFormData({...formData, discount_amount: parseFloat(e.target.value) || 0})}
              />
            </div>

            {formData.discount_amount > 0 && (
              <div>
                <Label htmlFor="discount_reason">{t('discountReason')}</Label>
                <Input
                  id="discount_reason"
                  value={formData.discount_reason}
                  onChange={(e) => setFormData({...formData, discount_reason: e.target.value})}
                  placeholder="سبب التخفيض (مطلوب للتخفيضات)"
                />
              </div>
            )}

            <div>
              <Label htmlFor="notes">ملاحظات</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
              />
            </div>

            {/* Enhanced Document Upload Section */}
            <div className="bg-orange-50 border-2 border-orange-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="text-orange-600 text-lg mr-2">📄</span>
                <Label className="font-bold text-orange-800">
                  وثيقة العملية *
                  <span className="text-red-600 mr-1">(إجباري)</span>
                </Label>
              </div>
              
              {/* Document Type Selection */}
              <div className="mb-3">
                <Label className="text-sm font-medium text-orange-800">نوع الوثيقة المطلوبة:</Label>
                <select
                  value={formData.document_type || 'image'}
                  onChange={(e) => {
                    const docType = e.target.value;
                    setFormData({
                      ...formData, 
                      document_type: docType,
                      document_image: null // Reset file when changing type
                    });
                    // Clear file input
                    const fileInput = document.getElementById('document_image');
                    if (fileInput) fileInput.value = '';
                  }}
                  className="w-full p-2 border border-orange-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                >
                  <option value="image">🖼️ صورة (JPG, PNG, GIF)</option>
                  <option value="pdf">📄 ملف PDF</option>
                </select>
              </div>
              
              <Input
                id="document_image"
                type="file"
                accept={formData.document_type === 'pdf' ? '.pdf' : 'image/jpeg,image/jpg,image/png,image/gif'}
                onChange={(e) => {
                  const file = e.target.files[0];
                  if (file) {
                    const docType = formData.document_type || 'image';
                    
                    // Validate file type based on selection
                    if (docType === 'pdf' && file.type !== 'application/pdf') {
                      alert('يرجى اختيار ملف PDF');
                      e.target.value = '';
                      return;
                    } else if (docType === 'image' && !file.type.startsWith('image/')) {
                      alert('يرجى اختيار ملف صورة');
                      e.target.value = '';
                      return;
                    }
                    
                    // Validate file size (5MB for PDF, 2MB for images)
                    const maxSize = docType === 'pdf' ? 5 * 1024 * 1024 : 2 * 1024 * 1024;
                    if (file.size > maxSize) {
                      alert(`حجم الملف يجب أن يكون أقل من ${docType === 'pdf' ? '5' : '2'} ميجابايت`);
                      e.target.value = '';
                      return;
                    }

                    // Validate file extension
                    const fileExtension = file.name.split('.').pop().toLowerCase();
                    const allowedExtensions = docType === 'pdf' ? ['pdf'] : ['jpg', 'jpeg', 'png', 'gif'];
                    if (!allowedExtensions.includes(fileExtension)) {
                      alert(`نوع الملف غير مدعوم. يرجى اختيار ${docType === 'pdf' ? 'ملف PDF' : 'صورة JPG، PNG، أو GIF'}`);
                      e.target.value = '';
                      return;
                    }

                    setFormData({...formData, document_image: file});
                  } else {
                    setFormData({...formData, document_image: null});
                  }
                }}
                className="mt-1"
                required
              />
              
              <div className="mt-2 text-xs text-orange-700">
                <p><strong>مطلوب:</strong> يجب رفع وثيقة مرتبطة بهذه العملية</p>
                <p><strong>أمثلة:</strong> تذكرة، وصل، عقد، فاتورة، إلخ</p>
                <p><strong>المواصفات:</strong> 
                  {formData.document_type === 'pdf' 
                    ? 'ملف PDF، أقل من 5 ميجابايت' 
                    : 'صورة JPG/PNG/GIF، أقل من 2 ميجابايت'
                  }
                </p>
              </div>

              {/* Document Preview */}
              {formData.document_image && (
                <div className="mt-3 text-center">
                  <p className="text-sm font-medium text-green-700 mb-2">
                    ✅ تم اختيار الوثيقة: {formData.document_type === 'pdf' ? 'ملف PDF' : 'صورة'}
                  </p>
                  
                  {formData.document_type === 'image' ? (
                    <img 
                      src={URL.createObjectURL(formData.document_image)}
                      alt="معاينة الصورة"
                      className="w-24 h-24 object-cover rounded-lg border mx-auto"
                    />
                  ) : (
                    <div className="w-24 h-24 bg-red-50 rounded-lg border border-red-200 mx-auto flex items-center justify-center">
                      <div className="text-center">
                        <span className="text-red-600 text-2xl">📄</span>
                        <p className="text-xs text-red-800 mt-1">PDF</p>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-gray-600 mt-1">
                    {formData.document_image.name} ({Math.round(formData.document_image.size / 1024)} KB)
                  </p>
                </div>
              )}
            </div>

            {/* NEW: Payment System */}
            <div className="bg-brand-50 border-2 border-brand-200 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="text-brand-600 text-lg mr-2">💰</span>
                <Label className="font-bold text-brand-800">نظام الدفع</Label>
              </div>
              
              <div className="space-y-4">
                {/* Payment Type Selection */}
                <div>
                  <Label>نوع الدفع</Label>
                  <select
                    value={formData.payment_type}
                    onChange={(e) => {
                      const newType = e.target.value;
                      setFormData({
                        ...formData, 
                        payment_type: newType,
                        // Reset payment fields when changing type
                        first_payment_amount: newType === 'full' ? '' : formData.first_payment_amount,
                        second_payment_amount: '',
                        third_payment_amount: '',
                        fourth_payment_amount: '',
                        second_payment_date: '',
                        third_payment_date: '',
                        fourth_payment_date: '',
                        // NEW: Flexible installment fields
                        flexible_installments: newType === 'flexible_installment' ? 6 : 0,
                        installment_period: 'monthly'
                      });
                    }}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="full">💰 دفع كامل</option>
                    <option value="installment">📅 دفع بالتقسيط (حتى 4 دفعات)</option>
                    <option value="flexible_installment">🌟 التقسيط المريح (مرن)</option>
                  </select>
                </div>

                {/* Full Payment Message */}
                {formData.payment_type === 'full' && (
                  <div className="bg-green-100 p-3 rounded-lg">
                    <p className="text-green-800 text-sm">
                      ✅ <strong>دفع كامل:</strong> سيتم تسجيل دفع المبلغ كاملاً ({(selectedService?.base_price - formData.discount_amount || 0).toLocaleString()} دج)
                    </p>
                  </div>
                )}

                {/* Flexible Installment (NEW) */}
                {formData.payment_type === 'flexible_installment' && (
                  <div className="space-y-4">
                    <div className="bg-green-100 p-3 rounded-lg">
                      <p className="text-green-800 text-sm">
                        🌟 <strong>التقسيط المريح:</strong> خطة تقسيط مرنة حسب احتياجات العميل
                      </p>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>عدد الدفعات</Label>
                        <select
                          value={formData.flexible_installments}
                          onChange={(e) => setFormData({...formData, flexible_installments: parseInt(e.target.value)})}
                          className="w-full p-2 border border-gray-300 rounded-md"
                        >
                          <option value="3">3 دفعات (3 أشهر)</option>
                          <option value="4">4 دفعات (4 أشهر)</option>
                          <option value="5">5 دفعات (5 أشهر)</option>
                          <option value="6">6 دفعات (6 أشهر)</option>
                          <option value="9">9 دفعات (9 أشهر)</option>
                          <option value="12">12 دفعة (سنة)</option>
                        </select>
                      </div>

                      <div>
                        <Label>الدفعة الأولى (دج)</Label>
                        <Input
                          type="number"
                          value={formData.first_payment_flexible}
                          onChange={(e) => setFormData({...formData, first_payment_flexible: e.target.value})}
                          placeholder="مبلغ الدفعة الأولى"
                        />
                      </div>
                    </div>

                    {/* Fixed Calculation */}
                    <div className="bg-white p-3 rounded-lg border border-green-300">
                      <h4 className="font-bold text-green-800 mb-2">📋 خطة التقسيط المريح (مُصححة):</h4>
                      {(() => {
                        const finalPrice = (selectedService?.base_price || 0) - (formData.discount_amount || 0);
                        const firstPayment = parseFloat(formData.first_payment_flexible) || 0;
                        const installments = parseInt(formData.flexible_installments) || 3;
                        
                        // CORRECTED: Equal rounded payments
                        const paymentAmount = Math.round(finalPrice / installments);  // Rounded payment
                        const lastPaymentAmount = finalPrice - (paymentAmount * (installments - 1));  // Adjusted last payment
                        
                        // Calculate total
                        const calculatedTotal = paymentAmount * (installments - 1) + lastPaymentAmount;
                        const difference = Math.abs(calculatedTotal - finalPrice);
                        
                        return (
                          <div className="space-y-2">
                            <div className="grid grid-cols-4 gap-3 text-sm">
                              <div>
                                <span className="text-gray-600">السعر النهائي:</span>
                                <div className="font-bold text-brand-600">{finalPrice.toLocaleString()} دج</div>
                              </div>
                              <div>
                                <span className="text-gray-600">كل دفعة:</span>
                                <div className="font-bold text-green-600">{paymentAmount.toLocaleString()} دج</div>
                              </div>
                              <div>
                                <span className="text-gray-600">الدفعة الأخيرة:</span>
                                <div className="font-bold text-purple-600">{lastPaymentAmount.toLocaleString()} دج</div>
                              </div>
                              <div>
                                <span className="text-gray-600">إجمالي محسوب:</span>
                                <div className={`font-bold ${difference < 1 ? 'text-green-600' : 'text-red-600'}`}>
                                  {calculatedTotal.toLocaleString()} دج
                                </div>
                              </div>
                            </div>
                            
                            <div className="bg-brand-100 p-2 rounded-lg">
                              <p className="text-brand-600 text-xs">
                                📋 خطة مدورة: {installments - 1} دفعات × {paymentAmount.toLocaleString()} دج + دفعة أخيرة {lastPaymentAmount.toLocaleString()} دج
                              </p>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                )}

                {/* Original Installment Payments (4 payments max) */}
                {formData.payment_type === 'installment' && (
                  <div className="space-y-4">
                    <div className="bg-yellow-100 p-3 rounded-lg">
                      <p className="text-yellow-800 text-sm">
                        📅 <strong>دفع بالتقسيط:</strong> سيتم تسجيل الدفعة الأولى الآن. يمكن إضافة الدفعات الأخرى لاحقاً عبر التعديل.
                      </p>
                    </div>

                    {/* Original 4-payment system code continues here */}
                    <div className="bg-white p-3 border border-brand-200 rounded-lg">
                      <Label className="font-medium text-brand-800 mb-2 block">💰 الدفعة الأولى (مطلوبة)</Label>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">المبلغ (دج)</Label>
                          <Input
                            type="number"
                            value={formData.first_payment_amount}
                            onChange={(e) => setFormData({...formData, first_payment_amount: e.target.value})}
                            placeholder="مبلغ الدفعة الأولى"
                            min="0"
                            max={selectedService?.base_price - formData.discount_amount}
                            step="0.01"
                            required
                          />
                        </div>
                        <div>
                          <Label className="text-sm">التاريخ</Label>
                          <Input
                            type="date"
                            value={formData.first_payment_date}
                            onChange={(e) => setFormData({...formData, first_payment_date: e.target.value})}
                            required
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => {
                setShowAddDialog(false);
                setSelectedService(null);
                setFormData({
                  service_id: '',
                  client_id: '',
                  base_price: '',
                  discount_amount: 0,
                  discount_reason: '',
                  notes: '',
                  document_image: null,
                  // Reset payment fields
                  payment_type: 'full',
                  first_payment_amount: '',
                  first_payment_date: new Date().toISOString().split('T')[0],
                  second_payment_amount: '',
                  second_payment_date: '',
                  third_payment_amount: '',
                  third_payment_date: '',
                  fourth_payment_amount: '',
                  fourth_payment_date: ''
                });
              }}>
                {t('cancel')}
              </Button>
              <Button type="submit" className="bg-brand-600 hover:bg-brand-700 text-white">
                {editingOperation ? '✏️ تحديث العملية' : '💾 حفظ العملية'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Print Preview Modal — shows the ACTUAL PDF that will be printed,
          rendered inline via <iframe>. The user can scroll, zoom, then hit
          "Print Now" which prints from the same iframe (no second download). */}
      <Dialog open={showPrintPreview} onOpenChange={setShowPrintPreview}>
        <DialogContent className="sm:max-w-[920px] max-h-[95vh] overflow-hidden p-0" dir="rtl">
          <DialogHeader className="px-5 pt-5">
            <DialogTitle className="text-center text-xl">🖨️ معاينة الوصل قبل الطباعة</DialogTitle>
            <p className="text-xs text-gray-500 text-center mt-1">
              معاينة فعلية لشكل الـ PDF النهائي (قالب Premium Gold). يمكنك التمرير والتكبير ثم الطباعة مباشرة.
            </p>
          </DialogHeader>

          {selectedOperationForPrint && (() => {
            const token = localStorage.getItem('token') || '';
            const pdfUrl = `${API}/daily-operations/${selectedOperationForPrint.id}/print?token=${encodeURIComponent(token)}&_=${Date.now()}#zoom=page-fit`;
            const xlsxUrl = `${API}/daily-operations/${selectedOperationForPrint.id}/print?token=${encodeURIComponent(token)}&format=xlsx&_=${Date.now()}`;
            return (
              <div className="flex flex-col h-[78vh]">
                {/* Embedded real PDF preview */}
                <iframe
                  src={pdfUrl}
                  title="PDF Preview"
                  className="flex-1 w-full border-t border-b"
                  data-testid="receipt-pdf-iframe"
                  id="receipt-pdf-iframe"
                />

                {/* Action bar */}
                <div className="flex justify-between items-center gap-2 px-5 py-3 bg-gray-50 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowPrintPreview(false);
                      setSelectedOperationForPrint(null);
                    }}
                    data-testid="receipt-preview-cancel"
                  >
                    ❌ إغلاق
                  </Button>
                  <div className="flex gap-2 flex-wrap">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = xlsxUrl;
                        a.download = `receipt_${selectedOperationForPrint.operation_no}.xlsx`;
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => a.remove(), 1000);
                      }}
                      data-testid="receipt-download-xlsx"
                    >
                      📊 تنزيل Excel
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => {
                        const a = document.createElement('a');
                        a.href = pdfUrl.replace(/#.*$/, '');
                        a.download = `receipt_${selectedOperationForPrint.operation_no}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        setTimeout(() => a.remove(), 1000);
                      }}
                      data-testid="receipt-download-pdf"
                    >
                      📥 تنزيل PDF
                    </Button>
                    <Button
                      onClick={() => {
                        // Trigger print on the embedded iframe — the user
                        // sees the SAME content they're previewing.
                        try {
                          const frame = document.getElementById('receipt-pdf-iframe');
                          if (frame && frame.contentWindow) {
                            frame.contentWindow.focus();
                            frame.contentWindow.print();
                          } else {
                            window.open(pdfUrl, '_blank');
                          }
                        } catch (e) {
                          // Cross-origin print fallback — open in new tab
                          window.open(pdfUrl, '_blank');
                        }
                      }}
                      className="bg-brand-600 hover:bg-brand-700 text-white"
                      data-testid="receipt-print-now"
                    >
                      🖨️ طباعة الآن
                    </Button>
                  </div>
                </div>
              </div>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Payment Dialog */}
      <Dialog open={showPaymentDialog} onOpenChange={setShowPaymentDialog}>
        <DialogContent className="sm:max-w-[800px] max-h-[90vh] overflow-y-auto" dir="rtl">
          <DialogHeader>
            <DialogTitle>💰 إضافة دفعة للعملية</DialogTitle>
          </DialogHeader>
          {selectedOperationForPayment && (
            <div>
              {/* Operation Details */}
              <div className="bg-gray-50 p-4 rounded-lg mb-4">
                <h4 className="font-bold mb-2">تفاصيل العملية:</h4>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><strong>رقم العملية:</strong> {selectedOperationForPayment.operation_no}</div>
                  <div><strong>اسم العميل:</strong> {clients.find(c => c.id === selectedOperationForPayment.client_id)?.name || 'غير محدد'}</div>
                  <div><strong>الخدمة:</strong> {selectedOperationForPayment.service_name}</div>
                  <div><strong>المبلغ الإجمالي:</strong> {selectedOperationForPayment.final_price.toLocaleString()} دج</div>
                  {(() => {
                    const paymentStatus = operationPaymentStatuses[selectedOperationForPayment.id];
                    return paymentStatus && (
                      <>
                        <div><strong>المدفوع:</strong> {paymentStatus.total_paid.toLocaleString()} دج</div>
                        <div><strong>المتبقي:</strong> <span className="text-red-600 font-bold">{paymentStatus.remaining_amount.toLocaleString()} دج</span></div>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Payment Form */}
              <form onSubmit={handlePaymentSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="payment-method">طريقة الدفع *</Label>
                  <Select value={paymentFormData.method} onValueChange={(value) => setPaymentFormData({...paymentFormData, method: value})}>
                    <SelectTrigger id="payment-method">
                      <SelectValue placeholder="اختر طريقة الدفع" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">💵 نقدي</SelectItem>
                      <SelectItem value="check">📄 شيك</SelectItem>
                      <SelectItem value="bank_transfer">🏦 تحويل بنكي</SelectItem>
                      <SelectItem value="credit_card">💳 بطاقة ائتمان</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Enhanced Payment Calculation Section */}
                <div className="bg-brand-50 p-4 rounded-lg border border-brand-200">
                  <h5 className="font-bold text-brand-800 mb-3 text-center">💰 حاسبة السداد المتقدمة</h5>
                  
                  {/* Operation Summary */}
                  <div className="bg-white p-3 rounded-lg mb-4">
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">المبلغ الإجمالي:</span>
                        <div className="font-bold text-brand-600">
                          {selectedOperationForPayment?.final_price?.toLocaleString() || 0} دج
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">المدفوع سابقاً:</span>
                        <div className="font-bold text-green-600">
                          {(operationPaymentStatuses[selectedOperationForPayment?.id]?.total_paid || 0).toLocaleString()} دج
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">المبلغ المتبقي:</span>
                        <div className="font-bold text-orange-600">
                          {(operationPaymentStatuses[selectedOperationForPayment?.id]?.remaining_amount || selectedOperationForPayment?.final_price || 0).toLocaleString()} دج
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Options */}
                  <div className="space-y-3">
                    <Label className="font-medium text-brand-800">اختر نوع الدفعة:</Label>
                    
                    {/* Quick Payment Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                      {/* Pay Remaining Amount */}
                      <Button
                        type="button"
                        onClick={() => {
                          const remaining = operationPaymentStatuses[selectedOperationForPayment?.id]?.remaining_amount || selectedOperationForPayment?.final_price || 0;
                          setPaymentFormData({...paymentFormData, amount: remaining.toString()});
                        }}
                        className="bg-green-600 hover:bg-green-700 text-white text-sm"
                      >
                        💰 دفع المبلغ المتبقي كاملاً
                      </Button>
                      
                      {/* Custom Amount */}
                      <Button
                        type="button"
                        onClick={() => setPaymentFormData({...paymentFormData, amount: ''})}
                        variant="outline"
                        className="text-brand-600 border-brand-300 text-sm"
                      >
                        ✏️ مبلغ مخصص
                      </Button>
                    </div>

                    {/* Installment Options */}
                    <div className="bg-yellow-50 p-3 rounded-lg">
                      <Label className="font-medium text-yellow-800 mb-2 block">📅 خيارات التقسيط السريع:</Label>
                      <div className="grid grid-cols-4 gap-2">
                        {(() => {
                          const remaining = operationPaymentStatuses[selectedOperationForPayment?.id]?.remaining_amount || selectedOperationForPayment?.final_price || 0;
                          const quarter = Math.round(remaining / 4);
                          const half = Math.round(remaining / 2);
                          const threeQuarter = Math.round(remaining * 0.75);
                          
                          return (
                            <>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setPaymentFormData({...paymentFormData, amount: quarter.toString()})}
                                className="text-xs"
                              >
                                25%<br/>({quarter.toLocaleString()} دج)
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setPaymentFormData({...paymentFormData, amount: half.toString()})}
                                className="text-xs"
                              >
                                50%<br/>({half.toLocaleString()} دج)
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setPaymentFormData({...paymentFormData, amount: threeQuarter.toString()})}
                                className="text-xs"
                              >
                                75%<br/>({threeQuarter.toLocaleString()} دج)
                              </Button>
                              <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={() => setPaymentFormData({...paymentFormData, amount: remaining.toString()})}
                                className="text-xs"
                              >
                                100%<br/>({remaining.toLocaleString()} دج)
                              </Button>
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  </div>

                  {/* Manual Amount Input */}
                  <div className="grid grid-cols-2 gap-4">
                    {/* Payment Amount */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">مبلغ هذه الدفعة (دج) *</Label>
                      <Input
                        type="number"
                        value={paymentFormData.amount}
                        onChange={(e) => setPaymentFormData({...paymentFormData, amount: e.target.value})}
                        placeholder="أدخل مبلغ الدفعة"
                        max={operationPaymentStatuses[selectedOperationForPayment?.id]?.remaining_amount || selectedOperationForPayment?.final_price}
                        min="0"
                        step="0.01"
                        required
                        className="font-bold text-green-700"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        الحد الأقصى: {(operationPaymentStatuses[selectedOperationForPayment?.id]?.remaining_amount || selectedOperationForPayment?.final_price || 0).toLocaleString()} دج
                      </p>
                    </div>

                    {/* Already Paid */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">المدفوع سابقاً (دج)</Label>
                      <Input
                        type="number"
                        value={(() => {
                          const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                          return paymentStatus?.total_paid || 0;
                        })()}
                        readOnly
                        className="bg-gray-100 font-bold text-green-700"
                      />
                    </div>

                    {/* Current Payment Amount */}
                    <div>
                      <Label htmlFor="payment-amount" className="text-sm font-medium text-red-700">
                        المبلغ المراد دفعه الآن (دج) *
                      </Label>
                      <Input
                        id="payment-amount"
                        type="number"
                        step="0.01"
                        min="0"
                        max={(() => {
                          const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                          return paymentStatus?.remaining_amount || selectedOperationForPayment?.final_price || 0;
                        })()}
                        value={paymentFormData.amount}
                        onChange={(e) => {
                          const currentPayment = parseFloat(e.target.value) || 0;
                          const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                          const maxAllowed = paymentStatus?.remaining_amount || selectedOperationForPayment?.final_price || 0;
                          
                          // Prevent payment exceeding remaining amount
                          if (currentPayment > maxAllowed) {
                            alert(`💡 لا يمكن دفع أكثر من المبلغ المتبقي: ${maxAllowed.toLocaleString()} دج`);
                            return;
                          }
                          
                          setPaymentFormData({...paymentFormData, amount: e.target.value});
                        }}
                        placeholder="أدخل المبلغ المراد دفعه"
                        className="border-red-300 focus:border-red-500"
                        required
                      />
                    </div>

                    {/* Remaining After This Payment */}
                    <div>
                      <Label className="text-sm font-medium text-gray-700">الباقي بعد هذه الدفعة (دج)</Label>
                      <Input
                        type="number"
                        value={(() => {
                          const currentPayment = parseFloat(paymentFormData.amount) || 0;
                          const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                          const remaining = (paymentStatus?.remaining_amount || selectedOperationForPayment?.final_price || 0) - currentPayment;
                          return Math.max(0, remaining);
                        })()}
                        readOnly
                        className={(() => {
                          const currentPayment = parseFloat(paymentFormData.amount) || 0;
                          const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                          const remaining = (paymentStatus?.remaining_amount || selectedOperationForPayment?.final_price || 0) - currentPayment;
                          return remaining <= 0 ? "bg-green-100 font-bold text-green-800" : "bg-yellow-100 font-bold text-orange-700";
                        })()}
                      />
                    </div>
                  </div>

                  {/* Payment Status Indicator */}
                  <div className="mt-3 p-2 rounded text-center text-sm font-medium">
                    {(() => {
                      const currentPayment = parseFloat(paymentFormData.amount) || 0;
                      const paymentStatus = operationPaymentStatuses[selectedOperationForPayment?.id];
                      const remaining = (paymentStatus?.remaining_amount || selectedOperationForPayment?.final_price || 0) - currentPayment;
                      
                      if (remaining <= 0) {
                        return <span className="bg-green-200 text-green-800 px-3 py-1 rounded-full">🟢 ✅ سيكون مسدد بالكامل</span>;
                      } else if (currentPayment > 0) {
                        return <span className="bg-yellow-200 text-yellow-800 px-3 py-1 rounded-full">🟡 ⏳ سداد جزئي - يتبقى {remaining.toLocaleString()} دج</span>;
                      } else {
                        return <span className="bg-gray-200 text-gray-800 px-3 py-1 rounded-full">⚪ أدخل مبلغ السداد</span>;
                      }
                    })()}
                  </div>
                </div>

                <div>
                  <Label htmlFor="payment-date">تاريخ الدفع *</Label>
                  <Input
                    id="payment-date"
                    type="date"
                    value={paymentFormData.payment_date}
                    onChange={(e) => setPaymentFormData({...paymentFormData, payment_date: e.target.value})}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="payment-notes">ملاحظات</Label>
                  <Input
                    id="payment-notes"
                    value={paymentFormData.notes}
                    onChange={(e) => setPaymentFormData({...paymentFormData, notes: e.target.value})}
                    placeholder="ملاحظات إضافية (اختياري)"
                  />
                </div>

                <div className="flex justify-end space-x-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowPaymentDialog(false)}>
                    إلغاء
                  </Button>
                  <Button type="submit" className="bg-green-600 hover:bg-green-700">
                    💰 إضافة الدفعة
                  </Button>
                </div>
              </form>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Image Upload Dialog */}
      <Dialog open={showImageDialog} onOpenChange={setShowImageDialog}>
        <DialogContent className="max-w-md" dir="rtl">
          <DialogHeader>
            <DialogTitle>
              📷 إدارة صورة الوثيقة
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedOperationForImage && (
              <div className="text-center">
                <h3 className="font-medium text-gray-900 mb-2">
                  عملية رقم: {selectedOperationForImage.operation_no}
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  الخدمة: {selectedOperationForImage.service_name}
                </p>
                <p className="text-sm text-gray-600 mb-4">
                  {selectedOperationForImage.document_image_url ? 'تغيير صورة الوثيقة' : 'إضافة صورة الوثيقة'}
                </p>
              </div>
            )}

            {/* Current Image Preview */}
            {selectedOperationForImage?.document_image_url && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">الصورة الحالية:</p>
                <img 
                  src={`${API}${selectedOperationForImage.document_image_url}`}
                  alt="الصورة الحالية"
                  className="w-24 h-24 object-cover rounded-lg border mx-auto"
                />
              </div>
            )}

            {/* File Input */}
            <div>
              <Label htmlFor="operationImageFile">اختر صورة جديدة:</Label>
              <Input
                id="operationImageFile"
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleImageFileChange}
                className="mt-1"
              />
              <p className="text-xs text-gray-500 mt-1">
                يدعم: JPG, PNG | الحد الأقصى: 2 ميجابايت
              </p>
            </div>

            {/* Selected Image Preview */}
            {selectedImage && (
              <div className="text-center">
                <p className="text-sm text-gray-600 mb-2">الصورة المختارة:</p>
                <img 
                  src={URL.createObjectURL(selectedImage)}
                  alt="معاينة الصورة"
                  className="w-32 h-32 object-cover rounded-lg border mx-auto"
                />
                <p className="text-xs text-gray-500 mt-1">
                  {selectedImage.name} ({Math.round(selectedImage.size / 1024)} KB)
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex justify-end space-x-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowImageDialog(false)}
                disabled={uploadingImage}
              >
                إلغاء
              </Button>
              <Button 
                onClick={handleImageUpload}
                disabled={!selectedImage || uploadingImage}
                className="bg-brand-600 hover:bg-brand-700"
              >
                {uploadingImage ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    جاري الرفع...
                  </div>
                ) : (
                  '📷 رفع الصورة'
                )}
              </Button>
            </div>

            {/* Instructions */}
            <div className="bg-brand-50 p-3 rounded-lg">
              <h4 className="font-medium text-brand-900 mb-2">📋 تعليمات:</h4>
              <ul className="text-sm text-brand-800 space-y-1">
                <li>• قم برفع صورة للوثيقة المرتبطة بهذه العملية</li>
                <li>• مثال: صورة تذكرة، وصل، عقد، فاتورة، إلخ</li>
                <li>• الصورة ستكون دليلاً على وجود العملية بالسعر المذكور</li>
                <li>• سيتم ضغط الصورة تلقائياً لتوفير المساحة</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="z-[60]" dir="rtl">{/* Higher z-index to appear above operation dialog */}
          <DialogHeader>
            <DialogTitle>➕ إضافة عميل جديد</DialogTitle>
            <DialogDescription>
              أدخل بيانات العميل الجديد بسرعة أثناء إنشاء العملية
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>الاسم الكامل *</Label>
                <Input
                  value={clientFormData.name}
                  onChange={(e) => setClientFormData({...clientFormData, name: e.target.value})}
                  placeholder="أدخل اسم العميل"
                  className="text-right"
                />
              </div>
              <div>
                <Label>رقم الهاتف *</Label>
                <Input
                  value={clientFormData.phone}
                  onChange={(e) => setClientFormData({...clientFormData, phone: e.target.value})}
                  placeholder="رقم الهاتف"
                  className="text-right"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label>رقم الهوية/جواز السفر</Label>
                <Input
                  value={clientFormData.cin_passport}
                  onChange={(e) => setClientFormData({...clientFormData, cin_passport: e.target.value})}
                  placeholder="رقم الهوية أو جواز السفر"
                  className="text-right"
                />
              </div>
              <div>
                <Label>البريد الإلكتروني</Label>
                <Input
                  type="email"
                  value={clientFormData.email}
                  onChange={(e) => setClientFormData({...clientFormData, email: e.target.value})}
                  placeholder="البريد الإلكتروني (اختياري)"
                  className="text-right"
                />
              </div>
            </div>

            <div>
              <Label>العنوان</Label>
              <Textarea
                value={clientFormData.address}
                onChange={(e) => setClientFormData({...clientFormData, address: e.target.value})}
                placeholder="عنوان العميل (اختياري)"
                className="text-right"
              />
            </div>

            <div className="flex justify-end space-x-2 rtl:space-x-reverse">
              <Button 
                variant="outline" 
                onClick={() => setShowAddClientDialog(false)}
              >
                إلغاء
              </Button>
              <Button 
                onClick={handleAddClient}
                disabled={!clientFormData.name || !clientFormData.phone}
                className="bg-green-600 hover:bg-green-700"
              >
                ➕ إضافة العميل
              </Button>
            </div>

            {/* Quick Instructions */}
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <h4 className="font-medium text-green-900 mb-2">💡 نصائح سريعة:</h4>
              <ul className="text-sm text-green-800 space-y-1">
                <li>• الاسم ورقم الهاتف مطلوبان فقط</li>
                <li>• باقي الحقول اختيارية ويمكن إضافتها لاحقاً</li>
                <li>• سيتم إضافة العميل واختياره تلقائياً في العملية</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Convert remaining to installments Dialog */}
      <Dialog open={showConvertDialog} onOpenChange={setShowConvertDialog}>
        <DialogContent className="max-w-md" data-testid="convert-installments-dialog">
          <DialogHeader>
            <DialogTitle className="text-right">💳 تحويل المبلغ المتبقي إلى الأقساط</DialogTitle>
            <DialogDescription className="text-right text-xs text-gray-600">
              قم بإنشاء وصل أقساط جديد للمبلغ المتبقي على هذه العملية. ستظهر العملية بإشارة "تحت الأقساط".
            </DialogDescription>
          </DialogHeader>
          {convertOperation && (
            <div className="space-y-4 py-2">
              <div className="bg-purple-50 border border-purple-200 rounded p-3 text-sm">
                <div><strong>العملية:</strong> {convertOperation.operation_no}</div>
                <div><strong>العميل:</strong> {convertOperation.client_name || '-'}</div>
                <div className="mt-1 text-purple-800">
                  <strong>المبلغ المراد تحويله:</strong> {convertRemaining.toLocaleString()} دج
                </div>
              </div>

              <div>
                <Label className="text-right block mb-1">عدد الأقساط</Label>
                <Input
                  type="number"
                  min="1"
                  max="60"
                  value={convertForm.number_of_payments}
                  onChange={e => setConvertForm({...convertForm, number_of_payments: e.target.value, custom_dates: []})}
                  className="text-right"
                  data-testid="convert-payments-count"
                />
              </div>

              <div>
                <Label className="text-right block mb-1">تكرار الدفع</Label>
                <select
                  value={convertForm.payment_frequency}
                  onChange={e => setConvertForm({...convertForm, payment_frequency: e.target.value, custom_dates: []})}
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                  data-testid="convert-frequency"
                >
                  <option value="weekly">أسبوعياً</option>
                  <option value="monthly">شهرياً</option>
                  <option value="quarterly">كل 3 أشهر</option>
                  <option value="custom">تواريخ مخصصة</option>
                </select>
              </div>

              {convertForm.payment_frequency !== 'custom' && (
                <div>
                  <Label className="text-right block mb-1">تاريخ بداية أول قسط</Label>
                  <Input
                    type="date"
                    value={convertForm.start_date}
                    onChange={e => setConvertForm({...convertForm, start_date: e.target.value})}
                    data-testid="convert-start-date"
                  />
                </div>
              )}

              {convertForm.payment_frequency === 'custom' && (
                <div className="space-y-2">
                  <Label className="text-right block">تواريخ الأقساط (مخصصة):</Label>
                  {Array.from({length: Number(convertForm.number_of_payments) || 0}).map((_, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 w-16">قسط {i+1}:</span>
                      <Input
                        type="date"
                        value={convertForm.custom_dates[i] || ''}
                        onChange={e => {
                          const arr = [...convertForm.custom_dates];
                          arr[i] = e.target.value;
                          setConvertForm({...convertForm, custom_dates: arr});
                        }}
                        data-testid={`convert-custom-date-${i}`}
                      />
                    </div>
                  ))}
                </div>
              )}

              <div className="bg-gray-50 rounded p-2 text-xs text-gray-600">
                💡 سيتم إنشاء وصل أقساط جديد بالمبلغ المتبقي. العملية ستظهر بإشارة "تحت الأقساط" مع الحفاظ على المبلغ المتبقي.
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setShowConvertDialog(false)}>إلغاء</Button>
                <Button
                  onClick={handleConvertSubmit}
                  disabled={converting}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="convert-submit-btn"
                >
                  {converting ? 'جاري التحويل...' : '💳 تحويل إلى الأقساط'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ============================================================
          Async Confirm Dialog (replaces window.confirm). Uses shadcn
          Dialog so it works in every browser/iframe — synchronous
          window.confirm is silently blocked in some environments
          (Chrome subframe modals, deployed iframes, popup-blockers).
          ============================================================ */}
      <Dialog
        open={confirmDialog.open}
        onOpenChange={(open) => { if (!open) closeConfirm(false); }}
      >
        <DialogContent className="max-w-md" data-testid="async-confirm-dialog">
          <DialogHeader>
            <DialogTitle>{confirmDialog.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-line text-right text-base text-gray-700 pt-2">
              {confirmDialog.message}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex flex-row-reverse gap-2 sm:gap-2">
            <Button
              variant={confirmDialog.danger ? 'destructive' : 'default'}
              onClick={() => closeConfirm(true)}
              data-testid="async-confirm-ok"
              className={confirmDialog.danger ? 'bg-red-600 hover:bg-red-700 text-white' : ''}
            >
              {confirmDialog.confirmLabel}
            </Button>
            <Button
              variant="outline"
              onClick={() => closeConfirm(false)}
              data-testid="async-confirm-cancel"
            >
              {confirmDialog.cancelLabel}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Async Prompt Dialog (replaces window.prompt) */}
      <Dialog
        open={promptDialog.open}
        onOpenChange={(open) => { if (!open) closePrompt(null); }}
      >
        <DialogContent className="max-w-md" data-testid="async-prompt-dialog">
          <DialogHeader>
            <DialogTitle>{promptDialog.title}</DialogTitle>
            <DialogDescription className="whitespace-pre-line text-right text-base text-gray-700 pt-2">
              {promptDialog.message}
            </DialogDescription>
          </DialogHeader>
          <div className="py-2">
            {promptDialog.multiline ? (
              <Textarea
                value={promptDialog.value}
                onChange={(e) => setPromptDialog((p) => ({ ...p, value: e.target.value }))}
                placeholder={promptDialog.placeholder}
                rows={3}
                className="text-right"
                data-testid="async-prompt-input"
                autoFocus
              />
            ) : (
              <Input
                value={promptDialog.value}
                onChange={(e) => setPromptDialog((p) => ({ ...p, value: e.target.value }))}
                placeholder={promptDialog.placeholder}
                className="text-right"
                data-testid="async-prompt-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') closePrompt(promptDialog.value);
                }}
              />
            )}
          </div>
          <DialogFooter className="flex flex-row-reverse gap-2 sm:gap-2">
            <Button
              onClick={() => closePrompt(promptDialog.value)}
              data-testid="async-prompt-ok"
            >
              تأكيد
            </Button>
            <Button
              variant="outline"
              onClick={() => closePrompt(null)}
              data-testid="async-prompt-cancel"
            >
              إلغاء
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Image Viewer (opened from "🖼️ صورة" button in the table) */}
      <Dialog
        open={showDocumentModal}
        onOpenChange={(open) => {
          if (!open) {
            setShowDocumentModal(false);
            setModalImageUrl('');
            setModalImageTitle('');
          }
        }}
      >
        <DialogContent
          className="max-w-3xl max-h-[90vh] overflow-y-auto"
          data-testid="document-image-viewer-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-right">
              {modalImageTitle || 'وثيقة العملية'}
            </DialogTitle>
            <DialogDescription className="text-right text-sm text-gray-600">
              يمكنك تكبير الصورة أو فتحها في تبويب جديد للطباعة.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center justify-center gap-3 py-2">
            {modalImageUrl ? (
              <>
                <img
                  src={modalImageUrl}
                  alt={modalImageTitle || 'وثيقة'}
                  className="max-w-full max-h-[65vh] rounded-lg border shadow"
                  data-testid="document-image-preview"
                  onError={(e) => {
                    e.currentTarget.style.display = 'none';
                    const fallback = document.getElementById('doc-image-fallback');
                    if (fallback) fallback.style.display = 'block';
                  }}
                />
                <div
                  id="doc-image-fallback"
                  style={{ display: 'none' }}
                  className="text-center text-red-600 bg-red-50 border border-red-200 rounded-lg p-4 w-full"
                >
                  ⚠️ تعذر تحميل الصورة من الخادم. قد يكون الملف غير موجود أو تم حذفه بعد آخر نشر.
                </div>
              </>
            ) : (
              <div className="text-gray-500">لا توجد وثيقة لعرضها</div>
            )}
          </div>
          <DialogFooter className="flex flex-row-reverse gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => window.open(modalImageUrl, '_blank')}
              disabled={!modalImageUrl}
              data-testid="document-open-newtab-btn"
            >
              فتح في تبويب جديد
            </Button>
            <Button
              onClick={() => {
                setShowDocumentModal(false);
                setModalImageUrl('');
                setModalImageTitle('');
              }}
              data-testid="document-close-btn"
            >
              إغلاق
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Duplicate Detection Warning Modal === */}
      <Dialog
        open={!!duplicateWarning}
        onOpenChange={(o) => { if (!o) setDuplicateWarning(null); }}
      >
        <DialogContent className="sm:max-w-[520px]" dir="rtl" data-testid="duplicate-warning-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-yellow-700">
              ⚠️ تنبيه: قد تكون عملية مكرَّرة
            </DialogTitle>
          </DialogHeader>
          {duplicateWarning && (
            <div className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-4 text-sm">
                <p className="font-bold mb-3">
                  هذا العميل عنده <span className="text-red-600">عملية مشابهة</span> خلال آخر 7 أيام:
                </p>
                <div className="space-y-1.5 text-gray-800">
                  <div>👤 <strong>العميل:</strong> {duplicateWarning.detail.client_name}</div>
                  <div>📦 <strong>الخدمة:</strong> {duplicateWarning.detail.service_name}</div>
                  {duplicateWarning.detail.existing?.operation_no && (
                    <div>🔖 <strong>رقم العملية السابقة:</strong>
                      <span className="font-mono mr-1">{duplicateWarning.detail.existing.operation_no}</span>
                    </div>
                  )}
                  {duplicateWarning.detail.existing?.date && (
                    <div>📅 <strong>تاريخها:</strong> {new Date(duplicateWarning.detail.existing.date).toLocaleDateString('ar-DZ')}</div>
                  )}
                  {typeof duplicateWarning.detail.existing?.final_price === 'number' && (
                    <div>💰 <strong>المبلغ:</strong> {duplicateWarning.detail.existing.final_price.toLocaleString()} دج</div>
                  )}
                  {duplicateWarning.detail.existing?.status && (
                    <div>📌 <strong>الحالة:</strong> {duplicateWarning.detail.existing.status}</div>
                  )}
                </div>
              </div>
              <p className="text-xs text-gray-600">
                💡 هل أنت متأكد من إضافة عملية جديدة لنفس العميل؟ إذا كانت قسطاً أو حجزاً
                مختلفاً يمكنك المتابعة، وإلا الأفضل الإلغاء لتجنّب التكرار.
              </p>
            </div>
          )}
          <DialogFooter className="flex justify-between gap-2 flex-wrap">
            <Button
              variant="outline"
              onClick={() => setDuplicateWarning(null)}
              data-testid="duplicate-cancel"
            >
              ❌ إلغاء (الأرجح مكرَّر)
            </Button>
            <Button
              onClick={confirmCreateDespiteDuplicate}
              className="bg-yellow-600 hover:bg-yellow-700 text-white font-bold"
              data-testid="duplicate-force-confirm"
            >
              ✅ نعم، أكمل الإضافة
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* === Refund Operation Modal === */}
      <Dialog
        open={!!refundOp}
        onOpenChange={(o) => { if (!o) setRefundOp(null); }}
      >
        <DialogContent className="sm:max-w-[560px]" dir="rtl" data-testid="op-refund-modal">
          <DialogHeader>
            <DialogTitle className="text-xl text-orange-700">
              💸 استرجاع عملية / خدمة
            </DialogTitle>
          </DialogHeader>
          {refundOp && (
            <div className="space-y-4">
              <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-sm">
                <div className="flex flex-wrap justify-between gap-2">
                  <div>🔖 <strong>الوصل:</strong> {refundOp.operation_no}</div>
                  <div>👤 <strong>العميل:</strong> {refundOp.client_name || '—'}</div>
                </div>
                <div className="flex flex-wrap justify-between gap-2 mt-1">
                  <div>📦 <strong>الخدمة:</strong> {(refundOp.service_name || '').slice(0, 40)}</div>
                  <div>💰 <strong>المبلغ النهائي:</strong> {Number(refundOp.final_price || 0).toLocaleString()} دج</div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-300 rounded-lg p-3 text-xs leading-relaxed text-yellow-900">
                ⚠️ سيتم إنشاء عملية مالية <b>سالبة</b> فوراً في القائمة + تظهر في
                التقارير لإنقاص الصندوق. العملية الأصلية تبقى ظاهرة مع شارة "🔄 مُسترَجَع".
              </div>

              <div>
                <Label>💰 مبلغ الاسترجاع (دج) *</Label>
                <Input type="number" min="1" step="0.01"
                  value={refundAmount}
                  onChange={(e) => setRefundAmount(e.target.value)}
                  data-testid="op-refund-amount"
                />
                <p className="text-[11px] text-gray-500 mt-1">
                  يمكن أن يكون أقل من السعر الأصلي (بعد خصم رسوم الإلغاء)
                </p>
              </div>

              <div>
                <Label>📝 سبب الاسترجاع *</Label>
                <textarea
                  className="w-full p-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                  rows={2}
                  value={refundReason}
                  onChange={(e) => setRefundReason(e.target.value)}
                  placeholder="مثال: ألغى العميل الخدمة بسبب ظرف صحي"
                  data-testid="op-refund-reason"
                />
              </div>

              <div>
                <Label>📷 صورة الوثيقة (إجبارية) *</Label>
                <Input type="file" accept="image/*" capture="environment"
                  onChange={(e) => setRefundImage(e.target.files?.[0] || null)}
                  data-testid="op-refund-image"
                />
                {refundImage && (
                  <p className="text-xs text-green-700 mt-1">
                    ✅ {refundImage.name} ({(refundImage.size / 1024).toFixed(0)} KB)
                  </p>
                )}
                <p className="text-[11px] text-red-700 mt-1 font-bold">
                  💡 JPG/PNG/WebP فقط — <span className="underline">PDF غير مسموح</span> (يُحذف بعد فترة)
                </p>
              </div>

              <div className="flex justify-between gap-2 pt-3 border-t flex-wrap">
                <Button
                  variant="outline"
                  onClick={() => setRefundOp(null)}
                  data-testid="op-refund-cancel"
                >
                  ❌ إلغاء
                </Button>
                <Button
                  onClick={submitOperationRefund}
                  disabled={refundBusy || !refundAmount || !refundReason || !refundImage}
                  className="bg-orange-600 hover:bg-orange-700 text-white font-bold"
                  data-testid="op-refund-submit"
                >
                  {refundBusy ? '⏳ جاري المعالجة...' : '✅ تأكيد الاسترجاع'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* 🤝 Subagent supplier-picker dialog (opens when approving a Daily Op
          from a B2B partner agency — choice C, mandatory). */}
      <SubagentApprovalDialog
        open={!!subagentApprovalTarget}
        onClose={() => setSubagentApprovalTarget(null)}
        onConfirm={handleSubagentApprovalConfirm}
        itemLabel={subagentApprovalTarget?.itemLabel}
        agencyName={subagentApprovalTarget?.agencyName}
        sellPrice={subagentApprovalTarget?.sellPrice}
        loading={subagentApprovalBusy}
      />

      {/* 🆕 Grant Credit Note (exceptional discount after full settlement) */}
      {creditNoteFor && (
        <GrantCreditNoteDialog
          bookingType="daily_operation"
          bookingId={creditNoteFor.id}
          bookingLabel={creditNoteFor.label}
          onClose={() => setCreditNoteFor(null)}
          onCreated={() => { setCreditNoteFor(null); fetchOperations(); }}
        />
      )}
    </div>
  );
};

// Daily Operations Reports Component


export default DailyOperationsManagement;
